import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useUser } from "../UserContext";
import ChildrensBookManuscriptEditor from "./ChildrensBookManuscriptEditor";
import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";
import NovelStoryWorkspaceNavigator from "./NovelStoryWorkspaceNavigator";
import { novelBookWorkspaceProgressV1 } from "../utils/novelBookWorkspaceProgressV1";
import NovelJourneyPilotWorkspaceV2C2 from "./NovelJourneyPilotWorkspaceV2C2";
import NovelFinalPrintApprovalV1 from "./NovelFinalPrintApprovalV1";
import NovelPrivatePreviewFinalHandoffV2C20 from "./NovelPrivatePreviewFinalHandoffV2C20";
import { finalPrintPilotAvailableForBook } from "../utils/novelFinalPrintPilotVisibilityV1";
import NovelStoryBookReview from "./NovelStoryBookReview";
import NovelStoryPublicationProof from "./NovelStoryPublicationProof";
import NovelStoryPublicationPreparation, { novelPublicationPreparationChecks, novelPrintSpecificationsPlanning } from "./NovelStoryPublicationPreparation";
import { buildNovelProductionSourceManifest } from "../utils/novelProductionSourceManifest";
import { novelWriterReadinessV3C3 } from "./NovelPublicationSimpleOverviewV3C3";
import { NovelStorySceneRichEditor, NovelStorySceneReadOnly } from "./NovelStorySceneManuscript";
import NextScenesConfirmDialog from "./NextScenesConfirmDialog";
import NovelWorkingCoverPairV1 from "./NovelWorkingCoverPairV1";
import { novelBookCoverConfirmationPlanV1, novelBookCoverConfirmationCopyV1 } from "../utils/novelBookCoverConfirmationV1";
import {
  NOVEL_FRONT_MATTER_TEMPLATES, NOVEL_BACK_MATTER_TEMPLATES,
  existingNovelMatterTemplate, hasReadableNovelMatterBody,
} from "../utils/novelMatterTemplateCatalog";

const CHAPTER_RANGE_SIZE = 20;

function clean(value) {
  return value == null ? "" : String(value).trim();
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function storyIdFromProject(project) {
  return clean(
    project?.legacyStoryId ||
      project?.storyId ||
      project?.projectId ||
      project?.id ||
      project?._id
  );
}

function sceneText(scene) {
  return clean(
    scene?.text ||
      scene?.body ||
      scene?.content ||
      scene?.sceneText
  );
}

function plainText(value) {
  return clean(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function wordCount(value) {
  const text = plainText(value);
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

function sceneIdentity(scene, index) {
  return (
    clean(
      scene?._id ||
        scene?.id ||
        scene?.sceneId ||
        scene?.localId
    ) || "draft-scene-" + (index + 1)
  );
}

function approvedCanonScenes(story) {
  return safeArray(story?.scenes)
    .filter(
      (scene) =>
        clean(scene?.type).toLowerCase() === "canon" &&
        clean(scene?.status).toLowerCase() === "approved"
    )
    .slice()
    .sort((a, b) => {
      const aNumber = Number(a?.sceneNumber);
      const bNumber = Number(b?.sceneNumber);

      if (
        Number.isFinite(aNumber) &&
        Number.isFinite(bNumber) &&
        aNumber !== bNumber
      ) {
        return aNumber - bNumber;
      }

      return (
        new Date(a?.createdAt || 0).getTime() -
        new Date(b?.createdAt || 0).getTime()
      );
    });
}

function storyTitle(story, project, isFR) {
  return (
    clean(story?.title) ||
    clean(project?.title) ||
    (isFR ? "Histoire sans titre" : "Untitled Story")
  );
}

function sceneTitle(scene, position, isFR) {
  return (
    clean(scene?.title || scene?.sceneTitle) ||
    (isFR ? "Scène " : "Scene ") + position
  );
}

function arrayMove(items, fromIndex, toIndex) {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const next = items.slice();
  const item = next.splice(fromIndex, 1)[0];
  next.splice(toIndex, 0, item);
  return next;
}

function buttonStyle(active) {
  return {
    border: active
      ? "1px solid #2563eb"
      : "1px solid rgba(100,116,139,0.28)",
    background: active ? "#eff6ff" : "#ffffff",
    color: active ? "#1d4ed8" : "#334155",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 850,
    cursor: "pointer",
  };
}

export default function NovelStoryBookViewDraft({
  project,
  lang = "en",
  onBack,
  onOpenWriterStudio,
  returnChapterId = "",
  onChildrensBookAddChapter,
  onChildrensBookAddScene,
  onChildrensBookRenameChapter,
  onChildrensBookMoveChapter,
  onChildrensBookBookSetup,
  onChildrensBookRenderMatter,
  childrensBookMatterCounts,
  childrensBookAudienceReady = false,
}) {
  const isFR = String(lang || "en").toLowerCase() === "fr";
  const { user: previewActor } = useUser();
  const storyId = storyIdFromProject(project);
  const isStructurePreview =
    project?.isStructurePreview === true;
  const isChildrensBridge = isStructurePreview &&
    project?.childrensBookPresentationBridgeV1E4C === true;
  const structurePreviewStory =
    project?.structurePreviewStory &&
    typeof project.structurePreviewStory === "object"
      ? project.structurePreviewStory
      : null;

  const [story, setStory] = useState(
    structurePreviewStory || null
  );
  const [loading, setLoading] = useState(
    Boolean(storyId && !structurePreviewStory)
  );
  const [error, setError] = useState("");
  const [interiorPreviewBusy, setInteriorPreviewBusy] = useState(false);
  const [interiorPreviewError, setInteriorPreviewError] = useState("");
  const [finalPrintApprovalOpen, setFinalPrintApprovalOpen] = useState(false);
  const [forewordDiagnosticBusy, setForewordDiagnosticBusy] = useState(false);
  const [forewordDiagnosticError, setForewordDiagnosticError] = useState("");
  const [forewordDiagnosticReport, setForewordDiagnosticReport] = useState(null);
  const [interiorPreviewUrl, setInteriorPreviewUrl] = useState("");
  const interiorPreviewRequestEpoch = useRef(0);
  // Release local preview PDF bytes when replaced or leaving Book Workspace.
  useEffect(() => () => {
    if (interiorPreviewUrl) URL.revokeObjectURL(interiorPreviewUrl);
  }, [interiorPreviewUrl]);
  const [savingStructure, setSavingStructure] = useState(false);
  const [structureMessage, setStructureMessage] = useState("");

  const [activeMatter, setActiveMatter] =
    useState(() => {
      if (typeof window === "undefined") return "manuscript";
      const section = new URLSearchParams(
        window.location.search || ""
      ).get("bookSection");
      return section === "front" || section === "back"
        ? section
        : "manuscript";
    });

  // Keep Matter deep links in sync with the visible Book Workspace tab.
  useEffect(() => {
    if (isStructurePreview || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (
      url.pathname !== "/my-projects" ||
      url.searchParams.get("storyId") !== String(storyId || "")
    ) return;
    const section =
      activeMatter === "front" || activeMatter === "back"
        ? activeMatter
        : "";
    if ((url.searchParams.get("bookSection") || "") === section) return;
    if (section) {
      url.searchParams.set("bookSection", section);
    } else {
      url.searchParams.delete("bookSection");
    }
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }, [activeMatter, isStructurePreview, storyId]);
  const [manuscriptWorkspace, setManuscriptWorkspace] =
    useState(isStructurePreview ? "book-structure" : "overview");
  // Only show return guidance after entering a step from Publication Preparation.
  const [publicationPreparationReturnActive, setPublicationPreparationReturnActive] = useState(false);
  const [activeChapterId, setActiveChapterId] =
    useState("");
  const [chapterNavigatorId, setChapterNavigatorId] =
    useState("");
  const [chapterRangeStart, setChapterRangeStart] =
    useState(0);

  const [chapters, setChapters] = useState([]);
  const [sceneRecords, setSceneRecords] = useState([]);
  const [unassignedSceneIds, setUnassignedSceneIds] =
    useState([]);
  const [unassignedSceneSearch, setUnassignedSceneSearch] = useState("");
  const [unassignedScenePage, setUnassignedScenePage] = useState(0);
  const [previewUnassignedSceneId, setPreviewUnassignedSceneId] = useState("");
  const [editingSceneDraft, setEditingSceneDraft] = useState(null);
  const [sceneEditSaving, setSceneEditSaving] = useState(false);
  const [sceneEditMessage, setSceneEditMessage] = useState("");
  const [chapterReviewOpen, setChapterReviewOpen] = useState(true);
  // Consume the explicit return target once per mounted book, after Chapters load.
  const nativeChapterReturnConsumedRef = useRef("");
  useEffect(() => {
    if (!isChildrensBridge) return;
    const target = project?.childrensBookReturnTargetV1E4D;
    if (!target?.sequence) return;
    if (target.sectionKind === "front" || target.sectionKind === "back") {
      setActiveMatter(target.sectionKind);
      setActiveChapterId("");
      return;
    }
    if (target.stage === "chapter-review" &&
        chapters.some(ch => ch.id === target.chapterId)) {
      setActiveMatter("manuscript");
      setChapterNavigatorId(target.chapterId);
      setActiveChapterId(target.chapterId);
      setChapterReviewOpen(true);
      setManuscriptWorkspace("chapter");
    }
  }, [isChildrensBridge, project?.childrensBookReturnTargetV1E4D, chapters]);
  const [wholeBookReviewOpen, setWholeBookReviewOpen] = useState(false);
  const [wholeBookReviewSection, setWholeBookReviewSection] = useState("front");

  const [nextChapterNumber, setNextChapterNumber] =
    useState(2);
  const [nextLocalSceneNumber, setNextLocalSceneNumber] =
    useState(1);

  const [editingChapterId, setEditingChapterId] =
    useState("");
  const [moveChapterPositionId, setMoveChapterPositionId] =
    useState("");
  const [moveScenePositionId, setMoveScenePositionId] =
    useState("");
  const [pendingChapterRemovalId, setPendingChapterRemovalId] =
    useState("");

  const [frontMatterItems, setFrontMatterItems] =
    useState([]);
  const [backMatterItems, setBackMatterItems] =
    useState([]);
  const [nextFrontMatterNumber, setNextFrontMatterNumber] =
    useState(1);
  const [nextBackMatterNumber, setNextBackMatterNumber] =
    useState(1);
  const [matterEditorItemId, setMatterEditorItemId] =
    useState("");
  const [matterEditorKind, setMatterEditorKind] =
    useState("");
  const [savingMatter, setSavingMatter] =
    useState(false);
  const [matterMessage, setMatterMessage] =
    useState("");
  const [matterHeaderNotice, setMatterHeaderNotice] = useState("");
  const [pendingMatterRemoval, setPendingMatterRemoval] = useState(null);
  const [matterReviewKind, setMatterReviewKind] = useState("");
  // Pending templates exist only in local editor state until written and saved.
  const [matterTemplateDraft, setMatterTemplateDraft] = useState(null);

  // Matter edits stay local until Done Editing, preventing each keystroke
  // from rebuilding Book Workspace and rehydrating the rich text editor.
  const pendingMatterEditRef = useRef(null);
  const matterEditorInitialEntry = useMemo(() => {
    if (!matterEditorKind || !matterEditorItemId) return null;
    const candidate =
      matterTemplateDraft?.kind === matterEditorKind &&
      matterTemplateDraft?.item?.id === matterEditorItemId
        ? matterTemplateDraft.item
        : (matterEditorKind === "front" ? frontMatterItems : backMatterItems)
            .find(row => row.id === matterEditorItemId);
    if (!candidate) return null;
    return {
      unitId: candidate.id,
      manuscriptText: String(candidate.text || ""),
      ...(candidate.manuscriptContent &&
      typeof candidate.manuscriptContent === "object" &&
      !Array.isArray(candidate.manuscriptContent)
        ? { manuscriptContent: candidate.manuscriptContent } : {}),
    };
  }, [matterEditorKind, matterEditorItemId,
      frontMatterItems, backMatterItems, matterTemplateDraft]);

  // Follow the proven Writer Studio modal scroll lock: keep the underlying
  // Book Workspace still while the Matter writing window owns scrolling.
  // Depend on editor identity, not draft text, to avoid jumps while typing.
  useEffect(() => {
    if (!matterEditorKind || !matterEditorItemId ||
        typeof document === "undefined") return undefined;
    const body = document.body;
    const root = document.documentElement;
    const previous = {
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      rootOverflow: root.style.overflow,
      rootOverscroll: root.style.overscrollBehavior,
      scrollbarGutter: root.style.scrollbarGutter,
    };
    root.style.scrollbarGutter = "stable";
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      root.style.scrollbarGutter = previous.scrollbarGutter;
      root.style.overflow = previous.rootOverflow;
      root.style.overscrollBehavior = previous.rootOverscroll;
      body.style.overflow = previous.bodyOverflow;
      body.style.overscrollBehavior = previous.bodyOverscroll;
    };
  }, [matterEditorKind, matterEditorItemId]);

  const [setupEditing, setSetupEditing] =
    useState(false);
  const [setupDraft, setSetupDraft] =
    useState(null);
  const [setupCoverFile, setSetupCoverFile] =
    useState(null);
  const [setupBackCoverFile, setSetupBackCoverFile] =
    useState(null);
  const [setupCoverPreviewUrl, setSetupCoverPreviewUrl] =
    useState("");
  const [setupBackCoverPreviewUrl, setSetupBackCoverPreviewUrl] =
    useState("");
  const [setupSaving, setSetupSaving] =
    useState(false);
  const [setupMessage, setSetupMessage] =
    useState("");
  const [pendingSetupCoverConfirmation, setPendingSetupCoverConfirmation] = useState(null);

  useEffect(() => {
    if (!setupCoverFile) {
      setSetupCoverPreviewUrl("");
      return undefined;
    }

    const objectUrl =
      URL.createObjectURL(setupCoverFile);
    setSetupCoverPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [setupCoverFile]);

  useEffect(() => {
    if (!setupBackCoverFile) {
      setSetupBackCoverPreviewUrl("");
      return undefined;
    }

    const objectUrl =
      URL.createObjectURL(setupBackCoverFile);
    setSetupBackCoverPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [setupBackCoverFile]);

  useEffect(() => {
    let active = true;

    async function loadStory() {
      if (structurePreviewStory) {
        setStory(structurePreviewStory);
        setLoading(false);
        setError("");
        return;
      }

      if (!storyId) {
        setLoading(false);
        setError(
          isFR
            ? "Identifiant de l’histoire manquant."
            : "Story identifier is missing."
        );
        return;
      }

      try {
        setLoading(true);
        setError("");

        const payload = await api.getStoryById(storyId);

        if (!active) return;
        setStory(payload || null);
      } catch (err) {
        if (!active) return;

        setStory(null);
        setError(
          clean(err?.message) ||
            (isFR
              ? "Impossible de charger cette histoire."
              : "Could not load this story.")
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    loadStory();

    return () => {
      active = false;
    };
  }, [storyId, isFR, structurePreviewStory]);

  useEffect(() => {
    if (!story) return;

    if (isStructurePreview && !isChildrensBridge) {
      setFrontMatterItems([]);
      setBackMatterItems([]);
      setNextFrontMatterNumber(1);
      setNextBackMatterNumber(1);
      return;
    }

    const savedFront = safeArray(story?.frontMatter).map(
      (item, index) => ({
        id:
          clean(item?.id) ||
          "front-matter-" + (index + 1),
        title:
          clean(item?.title) ||
          (isFR
            ? "Pages liminaires " + (index + 1)
            : "Front Matter " + (index + 1)),
        text: String(item?.text || ""),
        manuscriptContent:
          item?.manuscriptContent &&
          typeof item.manuscriptContent === "object" &&
          !Array.isArray(item.manuscriptContent)
            ? item.manuscriptContent
            : null,
      })
    );

    const savedBack = safeArray(story?.backMatter).map(
      (item, index) => ({
        id:
          clean(item?.id) ||
          "back-matter-" + (index + 1),
        title:
          clean(item?.title) ||
          (isFR
            ? "Élément de fin " + (index + 1)
            : "Back Matter " + (index + 1)),
        text: String(item?.text || ""),
        manuscriptContent:
          item?.manuscriptContent &&
          typeof item.manuscriptContent === "object" &&
          !Array.isArray(item.manuscriptContent)
            ? item.manuscriptContent
            : null,
      })
    );

    setFrontMatterItems(savedFront);
    setBackMatterItems(savedBack);
    setNextFrontMatterNumber(savedFront.length + 1);
    setNextBackMatterNumber(savedBack.length + 1);
  }, [story, isStructurePreview, isChildrensBridge, isFR]);

  const canonScenes = useMemo(
    () => approvedCanonScenes(story),
    [story]
  );

  const initialSceneEntries = useMemo(
    () =>
      canonScenes.map((scene, index) => ({
        id: sceneIdentity(scene, index),
        scene,
      })),
    [canonScenes]
  );

  const seedStructure = useCallback(() => {
    const ids = initialSceneEntries.map(
      (entry) => entry.id
    );

    const previewChapters =
      isStructurePreview
        ? safeArray(project?.structurePreviewChapters)
        : [];

    if (!isStructurePreview && safeArray(story?.chapters).length) {
      const knownIds = new Set(ids);
      const savedChapters = safeArray(story?.chapters).map(
        (chapter, index) => ({
          id:
            clean(chapter?.id) ||
            "chapter-" + (index + 1),
          title:
            clean(chapter?.title) ||
            (isFR
              ? "Chapitre " + (index + 1)
              : "Chapter " + (index + 1)),
          sceneIds: safeArray(chapter?.sceneIds)
            .map((sceneId) => clean(sceneId))
            .filter((sceneId) => knownIds.has(sceneId)),
        })
      );

      const assigned = new Set(
        savedChapters.flatMap(
          (chapter) => chapter.sceneIds
        )
      );

      setChapters(savedChapters);
      setUnassignedSceneIds(
        ids.filter((sceneId) => !assigned.has(sceneId))
      );
      setNextChapterNumber(savedChapters.length + 1);
      setChapterNavigatorId((current) =>
        savedChapters.some((chapter) => chapter.id === current)
          ? current
          : (savedChapters[0]?.id || "")
      );
      setActiveChapterId((current) =>
        savedChapters.some((chapter) => chapter.id === current) ? current : ""
      );
      return;
    }

    if (previewChapters.length) {
      const knownIds = new Set(ids);

      const seededChapters =
        previewChapters.map((chapter, index) => ({
          id:
            clean(chapter?.id) ||
            "preview-chapter-" + (index + 1),
          title:
            clean(chapter?.title) ||
            (isFR
              ? "Chapitre " + (index + 1)
              : "Chapter " + (index + 1)),
          sceneIds: safeArray(chapter?.sceneIds)
            .map((sceneId) => clean(sceneId))
            .filter((sceneId) => knownIds.has(sceneId)),
        }));

      const assigned = new Set(
        seededChapters.flatMap(
          (chapter) => chapter.sceneIds
        )
      );

      setChapters(seededChapters);
      setUnassignedSceneIds(
        ids.filter((sceneId) => !assigned.has(sceneId))
      );
      setNextChapterNumber(
        seededChapters.length + 1
      );
      setChapterNavigatorId(
        seededChapters[0]?.id || ""
      );
      setActiveChapterId(current => isChildrensBridge && seededChapters.some(c => c.id === current) ? current : "");
      return;
    }

    if (isChildrensBridge) {
      // Never seed a fictional Chapter for an empty Children's Book.
      setChapters([]);
      setUnassignedSceneIds(ids);
      setNextChapterNumber(1);
      setChapterNavigatorId('');
      setActiveChapterId('');
      return;
    }

    const firstChapter = {
      id: isStructurePreview
        ? "draft-chapter-1"
        : "chapter-1",
      title: isFR ? "Chapitre 1" : "Chapter 1",
      sceneIds: isStructurePreview ? [] : ids,
    };

    setChapters([firstChapter]);
    setUnassignedSceneIds(isStructurePreview ? ids : []);
    setNextChapterNumber(2);
    setChapterNavigatorId(firstChapter.id);
    setActiveChapterId("");
  }, [
    initialSceneEntries,
    isStructurePreview,
    isChildrensBridge,
    project,
    story,
    isFR,
  ]);

  useEffect(() => {
    if (!story) return;

    setSceneRecords(initialSceneEntries);
    setNextLocalSceneNumber(
      initialSceneEntries.length + 1
    );
    seedStructure();
  }, [
    storyId,
    story,
    initialSceneEntries,
    seedStructure,
  ]);

  useEffect(() => {
    if (isStructurePreview || project?.bookCategory !== "children_book") return;
    const targetId = clean(returnChapterId);
    if (!targetId || !storyId || !chapters.some(ch => ch.id === targetId)) return;
    const key = String(storyId) + ":" + targetId;
    if (nativeChapterReturnConsumedRef.current === key) return;
    nativeChapterReturnConsumedRef.current = key;
    setActiveMatter("manuscript");
    setActiveChapterId(targetId);
    setChapterNavigatorId(targetId);
    setChapterReviewOpen(true);
    setManuscriptWorkspace("chapter");
  }, [isStructurePreview, project?.bookCategory, storyId, returnChapterId, chapters]);

  const sceneById = useMemo(
    () =>
      new Map(
        sceneRecords.map((entry) => [
          entry.id,
          entry.scene,
        ])
      ),
    [sceneRecords]
  );

  // Search only approved Canon Scenes currently outside the saved Chapter plan.
  // Page size is deliberately small so large drafts do not obscure Chapters.
  const filteredUnassignedSceneIds = useMemo(() => {
    const search = clean(unassignedSceneSearch).toLowerCase();
    if (!search) return unassignedSceneIds;
    return unassignedSceneIds.filter((sceneId, index) => {
      const scene = sceneById.get(sceneId);
      return (
        sceneTitle(scene, index + 1, isFR) +
        " " +
        plainText(sceneText(scene))
      ).toLowerCase().includes(search);
    });
  }, [unassignedSceneIds, unassignedSceneSearch, sceneById, isFR]);

  const unassignedScenePageSize = 5;
  const unassignedScenePageCount = Math.max(
    1,
    Math.ceil(filteredUnassignedSceneIds.length / unassignedScenePageSize)
  );
  const visibleUnassignedScenePage = Math.min(
    unassignedScenePage,
    unassignedScenePageCount - 1
  );
  const pagedUnassignedSceneIds = filteredUnassignedSceneIds.slice(
    visibleUnassignedScenePage * unassignedScenePageSize,
    (visibleUnassignedScenePage + 1) * unassignedScenePageSize
  );

  const totalWords = useMemo(
    () =>
      sceneRecords.reduce(
        (sum, entry) =>
          sum + wordCount(sceneText(entry.scene)),
        0
      ),
    [sceneRecords]
  );

  const visibleChapters = useMemo(
    () =>
      chapters.slice(
        chapterRangeStart,
        chapterRangeStart + CHAPTER_RANGE_SIZE
      ),
    [chapters, chapterRangeStart]
  );

  const activeChapter = useMemo(
    () =>
      chapters.find(
        (chapter) => chapter.id === activeChapterId
      ) || null,
    [chapters, activeChapterId]
  );

  const bookCoverUrl = clean(
    story?.coverImageUrl ||
    story?.publicationCoverUrl ||
    project?.coverImageUrl ||
    project?.publicationCoverUrl
  );

  const bookBackCoverUrl = clean(
    story?.backCoverImageUrl ||
    project?.backCoverImageUrl
  );

  const bookAuthorName = clean(
    story?.publishedAuthorName ||
    story?.authorName ||
    story?.authorUsername ||
    story?.author ||
    project?.authorName ||
    project?.owner?.name ||
    project?.ownerUsername
  ) || (isFR ? "Auteur non renseigné" : "Author not set");

  const bookStoryType =
    story?.bookCategory === "children_book"
      ? (isFR ? "Livre jeunesse" : "Children's Book")
      : clean(story?.storyType || project?.storyType)
          .toLowerCase() === "short"
        ? (isFR ? "Nouvelle" : "Short Story")
        : (isFR ? "Roman / récit long" : "Novel / Long-form Story");

  const bookLanguage = (() => {
    const value = clean(
      story?.language || project?.language
    ).toLowerCase();

    if (value === "fr") {
      return isFR ? "Français" : "French";
    }

    if (value === "bilingual") {
      return isFR ? "Bilingue" : "Bilingual";
    }

    return isFR ? "Anglais" : "English";
  })();

  const bookWritingMode =
    clean(
      story?.writingMode ||
      story?.mode ||
      project?.writingMode ||
      project?.mode
    ) || "Solo";

  const bookVisibility =
    clean(
      story?.visibilityStatus ||
      project?.visibilityStatus
    ) || "public_preview";

  const savedBookFormat =
    story?.bookFormat &&
    typeof story.bookFormat === "object" &&
    !Array.isArray(story.bookFormat)
      ? story.bookFormat
      : {};

  // Read-only Book Setup guidance. Working-cover artwork is not an uploaded file.
  const bookSetupProgress = novelBookWorkspaceProgressV1({
    bookFormat: savedBookFormat,
    frontCoverUrl: bookCoverUrl,
    backCoverUrl: bookBackCoverUrl,
    chapterCount: chapters.length,
    sceneCount: sceneRecords.length,
    wordCount: totalWords,
    publicationStatus: story?.publicationStatus || project?.publicationStatus || "inDevelopment",
    isFR,
  });

  function bookFormatLabel(field, value) {
    const normalized =
      clean(value).toLowerCase();

    if (!normalized) {
      return isFR
        ? "Non renseigné"
        : "Not set yet";
    }

    const labels = {
      trimSize: {
        "8_x_8_in": "8 × 8 inches",
        "8.25_x_8.25_in": "8.25 × 8.25 inches",
        "8.5_x_8.5_in": "8.5 × 8.5 inches",
        "8_x_10_in": "8 × 10 inches",
        "8.5_x_11_in": "8.5 × 11 inches",
        "5.5_x_8.5_in": "5.5 × 8.5 inches",
        "6_x_9_in": "6 × 9 inches",
        a4: "A4",
        a5: "A5",
        custom_dimensions:
          clean(savedBookFormat.customTrimSize) ||
          (isFR ? "Dimensions personnalisées" : "Custom dimensions"),
        not_decided:
          isFR ? "Pas encore décidé" : "Not yet decided",
      },
      orientation: {
        portrait: isFR ? "Portrait" : "Portrait",
        landscape: isFR ? "Paysage" : "Landscape",
        square: isFR ? "Carré" : "Square",
      },
      bleed: {
        yes: isFR ? "Oui" : "Yes",
        no: isFR ? "Non" : "No",
      },
      publicationFormat: {
        print: isFR ? "Imprimé" : "Print",
        digital: isFR ? "Numérique" : "Digital",
        print_and_digital:
          isFR ? "Imprimé et numérique" : "Print and digital",
      },
      binding: { paperback: isFR ? "Broché" : "Paperback" },
      paperType: { white: isFR ? "Blanc" : "White", cream: isFR ? "Crème" : "Cream" },
      interiorColor: {
        black_and_white: isFR ? "Noir et blanc" : "Black & White",
        standard_color: isFR ? "Couleur standard" : "Standard Colour",
        premium_color: isFR ? "Couleur premium" : "Premium Colour",
      },
    };

    return (
      labels[field]?.[normalized] ||
      clean(value)
    );
  }

  function beginBookSetupEdit() {
    setSetupDraft({
      title:
        clean(story?.title || project?.title),
      forum:
        clean(story?.forum || project?.forum),
      storyType:
        clean(story?.storyType || project?.storyType) ||
        "longform",
      language:
        clean(story?.language || project?.language) ||
        "en",
      synopsis:
        clean(story?.synopsis || project?.synopsis),
      storyTeaser:
        clean(story?.storyTeaser ?? project?.storyTeaser),
      // Match the exact backend PDF source, not a possibly stale project card.
      printedAuthorName: clean(story?.publishedAuthorName || story?.authorName),
      coverImageUrl:
        bookCoverUrl,
      backCoverImageUrl:
        bookBackCoverUrl,
      childrensBookProfile: story?.bookCategory === "children_book"
        ? { ...(story?.childrensBookProfile || {}) } : null,
      bookFormat: {
        trimSize:
          clean(savedBookFormat.trimSize),
        customTrimSize:
          clean(savedBookFormat.customTrimSize),
        orientation:
          clean(savedBookFormat.orientation),
        bleed:
          clean(savedBookFormat.bleed),
        publicationFormat:
          clean(savedBookFormat.publicationFormat),
        binding: clean(savedBookFormat.binding),
        paperType: clean(savedBookFormat.paperType),
        interiorColor: clean(savedBookFormat.interiorColor),
      },
    });

    setSetupCoverFile(null);
    setSetupBackCoverFile(null);
    setPendingSetupCoverConfirmation(null);
    setSetupMessage("");
    setSetupEditing(true);
  }

  function updateSetupDraft(field, value) {
    setSetupDraft((current) => ({
      ...(current || {}),
      [field]: value,
    }));
  }

  function updateSetupFormat(field, value) {
    setSetupDraft((current) => ({
      ...(current || {}),
      bookFormat: {
        ...((current || {}).bookFormat || {}),
        [field]: value,
        ...(field === "trimSize" &&
        value !== "custom_dimensions"
          ? { customTrimSize: "" }
          : {}),
      },
    }));
  }

  async function saveBookSetup({ coverReplacementConfirmed = false } = {}) {
    if (
      !storyId ||
      !setupDraft ||
      setupSaving
    ) {
      return;
    }

    const title =
      clean(setupDraft.title);

    const forum =
      clean(setupDraft.forum);

    const teaser = clean(setupDraft.storyTeaser);
    const printedAuthorName = clean(setupDraft.printedAuthorName);
    const printedAuthorChanged = printedAuthorName !==
      clean(story?.publishedAuthorName || story?.authorName);
    if (printedAuthorChanged && (!printedAuthorName || printedAuthorName.length > 160)) {
      setSetupMessage(isFR
        ? "Le nom d’auteur imprimé est obligatoire (160 caractères maximum)."
        : "Printed author name is required (maximum 160 characters).");
      return;
    }
    const teaserWords = teaser ? teaser.split(/\s+/).length : 0;
    if (teaserWords > 90 || teaser.length > 900) {
      setSetupMessage(
        isFR
          ? "L’accroche doit contenir au maximum 90 mots et 900 caractères."
          : "Story Teaser must be no more than 90 words and 900 characters."
      );
      return;
    }

    if (!title || !forum) {
      setSetupMessage(
        isFR
          ? "Le titre et le genre sont obligatoires."
          : "Title and Genre are required."
      );
      return;
    }

    const printSettingsDraft = setupDraft.bookFormat || {};
    const printRequested = ["print", "print_and_digital"].includes(clean(printSettingsDraft.publicationFormat));
    if (printRequested &&
        ["standard_color", "premium_color"].includes(clean(printSettingsDraft.interiorColor)) &&
        clean(printSettingsDraft.paperType) === "cream") {
      setSetupMessage(isFR
        ? "La couleur standard ou premium nécessite du papier blanc."
        : "Standard or Premium Colour interiors require White paper.");
      return;
    }

    // Confirm first uploads over visual defaults AND replacement of saved images.
    // The default artwork remains a nonpersistent visual preview, not a fake saved URL.
    const frontReplacement = Boolean(setupCoverFile && clean(setupDraft.coverImageUrl));
    const backReplacement = Boolean(setupBackCoverFile && clean(setupDraft.backCoverImageUrl));
    const plannedCoverChange = novelBookCoverConfirmationPlanV1({
      frontFile: setupCoverFile, backFile: setupBackCoverFile,
      replaceFront: frontReplacement, replaceBack: backReplacement,
    });
    if (!coverReplacementConfirmed && plannedCoverChange.required) {
      setPendingSetupCoverConfirmation(plannedCoverChange);
      return; // No upload, PUT, or other mutation occurs before consent.
    }

    try {
      setSetupSaving(true);
      setSetupMessage(
        isFR
          ? "Enregistrement…"
          : "Saving Book Setup…"
      );

      let coverImageUrl =
        clean(setupDraft.coverImageUrl);

      let backCoverImageUrl =
        clean(setupDraft.backCoverImageUrl);

      if (setupCoverFile) {
        const uploaded =
          await api.uploadStoryCover(
            setupCoverFile
          );

        coverImageUrl =
          clean(
            uploaded?.url ||
            uploaded?.publicUrl
          );
      }

      if (setupBackCoverFile) {
        const uploadedBack =
          await api.uploadStoryCover(
            setupBackCoverFile
          );

        backCoverImageUrl =
          clean(
            uploadedBack?.url ||
            uploadedBack?.publicUrl
          );
      }

      const updated =
        await api.put(
          `/api/stories/${encodeURIComponent(
            storyId
          )}`,
          {
            title,
            forum,
            storyType:
              clean(setupDraft.storyType) ||
              "longform",
            language:
              clean(setupDraft.language) ||
              "en",
            synopsis:
              clean(setupDraft.synopsis),
            storyTeaser: teaser,
            ...(printedAuthorChanged
              ? { publishedAuthorName: printedAuthorName } : {}),
            coverImageUrl,
            backCoverImageUrl,
            ...(story?.bookCategory === "children_book"
              ? { childrensBookProfile: setupDraft.childrensBookProfile } : {}),
            bookFormat: {
              trimSize:
                clean(
                  setupDraft.bookFormat?.trimSize
                ),
              customTrimSize:
                clean(
                  setupDraft.bookFormat
                    ?.customTrimSize
                ),
              orientation:
                clean(
                  setupDraft.bookFormat
                    ?.orientation
                ),
              bleed:
                clean(
                  setupDraft.bookFormat?.bleed
                ),
              publicationFormat:
                clean(
                  setupDraft.bookFormat
                    ?.publicationFormat
                ),
              binding: clean(setupDraft.bookFormat?.binding),
              paperType: clean(setupDraft.bookFormat?.paperType),
              interiorColor: clean(setupDraft.bookFormat?.interiorColor),
            },
          }
        );

      setStory(updated || story);
      setSetupDraft(null);
      setSetupCoverFile(null);
      setSetupBackCoverFile(null);
      setSetupEditing(false);
      setSetupMessage(
        isFR
          ? "Book Setup enregistré."
          : "Book Setup saved."
      );
    } catch (err) {
      setSetupMessage(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer Book Setup."
            : "Could not save Book Setup."
        )
      );
    } finally {
      setSetupSaving(false);
    }
  }

  function cancelBookSetupEdit() {
    if (setupSaving) return;
    setPendingSetupCoverConfirmation(null);
    setSetupEditing(false);
    setSetupDraft(null);
    setSetupCoverFile(null);
    setSetupBackCoverFile(null);
    setSetupMessage("");
  }

  function chapterWords(chapter) {
    return safeArray(chapter?.sceneIds).reduce(
      (sum, sceneId) => {
        const scene = sceneById.get(sceneId);
        return sum + wordCount(sceneText(scene));
      },
      0
    );
  }

  function chapterStatus(chapter) {
    return chapter?.sceneIds?.length
      ? (isFR ? "En cours" : "In progress")
      : (isFR ? "Non commencé" : "Not started");
  }

  async function persistChapters(nextChapters) {
    if (isChildrensBridge) return false;
    if (isStructurePreview) {
      setStructureMessage(
        isFR
          ? "Aperçu uniquement · aucune sauvegarde"
          : "Preview only · nothing saved"
      );
      return true;
    }

    if (!storyId) return false;

    setSavingStructure(true);
    setStructureMessage(
      isFR ? "Enregistrement…" : "Saving…"
    );

    try {
      const response = await api(
        `/api/stories/${encodeURIComponent(storyId)}/chapters`,
        {
          method: "PATCH",
          body: JSON.stringify({
            chapters: nextChapters,
          }),
        }
      );

      const updatedStory =
        response?.story || null;

      if (updatedStory) {
        setStory(updatedStory);
      }

      setStructureMessage(
        isFR ? "Structure enregistrée" : "Structure saved"
      );

      return true;
    } catch (err) {
      setStructureMessage(
        clean(err?.message) ||
          (isFR
            ? "Échec de l’enregistrement de la structure."
            : "Could not save Chapter structure.")
      );
      return false;
    } finally {
      setSavingStructure(false);
    }
  }

  async function finishChapterEditing(chapterId) {
    if (isChildrensBridge) {
      if (editingChapterId !== chapterId) {setEditingChapterId(chapterId);return;}
      const edited=chapters.find(ch=>ch.id===chapterId);
      if(!edited) return;
      const saved=onChildrensBookRenameChapter?.({chapterId,title:edited.title});
      if(saved!==false) setEditingChapterId('');
      return;
    }
    if (editingChapterId !== chapterId) {
      setEditingChapterId(chapterId);
      return;
    }

    const ok = await persistChapters(chapters);
    if (ok) setEditingChapterId("");
  }

  async function addChapter() {
    if (isChildrensBridge) {
      onChildrensBookAddChapter?.();
      return;
    }
    const chapterNumber = nextChapterNumber;
    const chapter = {
      id:
        "chapter-" +
        Date.now().toString(36) +
        "-" +
        chapterNumber,
      title: isFR
        ? "Chapitre " + chapterNumber
        : "Chapter " + chapterNumber,
      sceneIds: [],
    };

    const next = [...chapters, chapter];
    setChapters(next);
    setNextChapterNumber((value) => value + 1);
    setChapterNavigatorId(chapter.id);
    await persistChapters(next);
  }

  function renameChapter(chapterId, title) {
    // Children’s Book temporarily edits the visual draft, and persists only on
    // Done Editing through the Children's Book Blueprint authority.
    setChapters((current) =>
      current.map((chapter) =>
        chapter.id === chapterId
          ? { ...chapter, title }
          : chapter
      )
    );
  }

  async function moveChapter(chapterId, direction) {
    if (isChildrensBridge) {
      const index=chapters.findIndex(ch=>ch.id===chapterId);
      if(index<0) return;
      onChildrensBookMoveChapter?.({chapterId,toIndex:index+(direction==='up'?-1:1)});
      return;
    }
    const index = chapters.findIndex(
      (chapter) => chapter.id === chapterId
    );
    if (index < 0) return;

    const target =
      direction === "up" ? index - 1 : index + 1;
    const next = arrayMove(chapters, index, target);

    if (next === chapters) return;
    setChapters(next);
    await persistChapters(next);
  }

  async function moveChapterToPosition(
    chapterId,
    rawPosition
  ) {
    if (isChildrensBridge) {
      onChildrensBookMoveChapter?.({chapterId,toIndex:Number(rawPosition)-1});
      setMoveChapterPositionId('');
      return;
    }
    const position = Number(rawPosition);
    if (!Number.isInteger(position)) return;

    const index = chapters.findIndex(
      (chapter) => chapter.id === chapterId
    );
    const target = position - 1;

    if (
      index < 0 ||
      target < 0 ||
      target >= chapters.length
    ) {
      return;
    }

    const next = arrayMove(chapters, index, target);
    setChapters(next);
    setMoveChapterPositionId("");
    await persistChapters(next);
  }

  async function duplicateChapter(chapterId) {
    if (isChildrensBridge) return;
    const index = chapters.findIndex(
      (chapter) => chapter.id === chapterId
    );
    if (index < 0) return;

    const source = chapters[index];
    const chapterNumber = nextChapterNumber;
    const copy = {
      id:
        "chapter-" +
        Date.now().toString(36) +
        "-" +
        chapterNumber,
      title:
        clean(source.title) +
        (isFR ? " — copie" : " — Copy"),
      // Structural duplication deliberately starts empty.
      sceneIds: [],
    };

    const next = chapters.slice();
    next.splice(index + 1, 0, copy);

    setChapters(next);
    setNextChapterNumber((value) => value + 1);
    await persistChapters(next);
  }

  function requestRemoveChapter(chapterId) {
    if (isChildrensBridge) return;
    const id = clean(chapterId);
    if (!id) return;
    setPendingChapterRemovalId(id);
  }

  function cancelRemoveChapter() {
    setPendingChapterRemovalId("");
  }

  async function confirmRemoveChapter() {
    if (isChildrensBridge) return;
    const chapterId =
      clean(pendingChapterRemovalId);

    if (!chapterId) return;

    setPendingChapterRemovalId("");
    await removeChapter(chapterId);
  }

  async function removeChapter(chapterId) {
    if (isChildrensBridge) return;
    const chapter = chapters.find(
      (item) => item.id === chapterId
    );
    if (!chapter) return;

    const nextUnassigned = [
      ...unassignedSceneIds,
      ...chapter.sceneIds.filter(
        (sceneId) => !unassignedSceneIds.includes(sceneId)
      ),
    ];

    const next = chapters.filter(
      (item) => item.id !== chapterId
    );

    setUnassignedSceneIds(nextUnassigned);
    setChapters(next);

    if (activeChapterId === chapterId) {
      setActiveChapterId("");
      setManuscriptWorkspace("book-structure");
    }

    if (chapterNavigatorId === chapterId) {
      setChapterNavigatorId(next[0]?.id || "");
    }

    await persistChapters(next);
  }

  // Open the precise saved Canon Scene inside Book Workspace. Never substitute
  // currentStudioScene or create another Scene while reviewing the book.
  function openSceneForEditing(sceneId) {
    if (isChildrensBridge) {
      const chapter=chapters.find(row=>safeArray(row.sceneIds).includes(sceneId));
      if (chapter && typeof onOpenWriterStudio === "function")
        onOpenWriterStudio({chapterId:chapter.id,sceneId});
      return;
    }
    const scene = sceneById.get(sceneId);
    if (!scene) return;
    const chapter = chapters.find(
      (entry) => safeArray(entry.sceneIds).includes(sceneId)
    );
    setEditingSceneDraft({
      sceneId,
      title: String(scene?.title || ""),
      text: String(scene?.text || ""),
      manuscriptContent: (
        scene?.manuscriptContent &&
        typeof scene.manuscriptContent === "object" &&
        !Array.isArray(scene.manuscriptContent)
      ) ? scene.manuscriptContent : null,
      originalChapterId: chapter?.id || "",
      targetChapterId: chapter?.id || "",
      originalTitle: String(scene?.title || ""),
      originalText: String(scene?.text || ""),
      originalManuscriptContent: (
        scene?.manuscriptContent &&
        typeof scene.manuscriptContent === "object" &&
        !Array.isArray(scene.manuscriptContent)
      ) ? scene.manuscriptContent : null,
      dirty: false,
    });
    setSceneEditMessage("");
  }

  function closeSceneEditor() {
    if (sceneEditSaving) return;
    if (
      editingSceneDraft?.dirty &&
      typeof window !== "undefined" &&
      !window.confirm(
        isFR
          ? "Abandonner les modifications non enregistrées ?"
          : "Discard unsaved Scene changes?"
      )
    ) {
      return;
    }
    setEditingSceneDraft(null);
    setSceneEditMessage("");
  }

  async function saveEditedScene() {
    const draft = editingSceneDraft;
    if (!draft || sceneEditSaving || !storyId || isStructurePreview) return;
    if (
      story?.proposalsOpen &&
      !clean(story?.writingMode).toLowerCase().includes("solo")
    ) {
      setSceneEditMessage(
        isFR
          ? "Canon est verrouillé pendant le tour de propositions."
          : "Canon is locked while proposals are open."
      );
      return;
    }

    const title = clean(draft.title);
    const text = String(draft.text || "").trim();
    if (!text) {
      setSceneEditMessage(
        isFR ? "Le texte de la scène est obligatoire." : "Scene text is required."
      );
      return;
    }

    setSceneEditSaving(true);
    setSceneEditMessage(isFR ? "Enregistrement de la scène…" : "Saving Scene…");

    let updatedStory = null;
    let canonSaveSucceeded = false;
    try {
      // Only edit Canon when its actual content changes. Chapter assignment
      // alone must not create a spurious Canon editing-history record.
      const sceneChanged = Boolean(
        title !== clean(draft.originalTitle) ||
        text !== String(draft.originalText || "").trim() ||
        JSON.stringify(draft.manuscriptContent || null) !==
          JSON.stringify(draft.originalManuscriptContent || null)
      );

      if (sceneChanged) {
        // Same owner-only Canon PATCH used by Writer Studio. Scene ID is fixed.
        const response = await api(
          `/api/stories/${encodeURIComponent(storyId)}/scenes/${encodeURIComponent(draft.sceneId)}`,
          {
            method: "PATCH",
            body: JSON.stringify({
              title,
              text,
              manuscriptContent: draft.manuscriptContent || null,
            }),
          }
        );
        canonSaveSucceeded = true;
        updatedStory = response?.story || null;
      }

      // Chapter placement is a second saved operation, not part of the Canon
      // edit. Never claim Canon failed after an acknowledged Canon save.
      if (draft.targetChapterId !== draft.originalChapterId) {
        const currentChapters =
          safeArray(updatedStory?.chapters).length
            ? updatedStory.chapters
            : chapters;
        const targetExists =
          !draft.targetChapterId ||
          currentChapters.some((chapter) => chapter.id === draft.targetChapterId);
        if (!targetExists) {
          throw new Error(
            isFR ? "Le chapitre sélectionné n’existe plus." : "The selected Chapter no longer exists."
          );
        }

        const nextChapters = currentChapters.map((chapter) => ({
          ...chapter,
          sceneIds: safeArray(chapter.sceneIds)
            .filter((id) => clean(id) !== draft.sceneId),
        })).map((chapter) => (
          chapter.id === draft.targetChapterId
            ? { ...chapter, sceneIds: [...chapter.sceneIds, draft.sceneId] }
            : chapter
        ));

        const placement = await api(
          `/api/stories/${encodeURIComponent(storyId)}/chapters`,
          {
            method: "PATCH",
            body: JSON.stringify({ chapters: nextChapters }),
          }
        );
        updatedStory = placement?.story || updatedStory;
        setChapters(nextChapters);
      }

      if (updatedStory) setStory(updatedStory);
      setEditingSceneDraft(null);
      setSceneEditMessage("");
      setStructureMessage(
        isFR ? "Scène enregistrée" : "Scene saved"
      );
    } catch (error) {
      if (canonSaveSucceeded) {
        // Canon prose is already confirmed server-side; placement may fail.
        if (updatedStory) setStory(updatedStory);
        setSceneEditMessage(
          (isFR
            ? "Texte de la scène enregistré, mais déplacement du chapitre non confirmé. "
            : "Scene text saved, but Chapter assignment was not confirmed. ") +
          clean(error?.message)
        );
      } else {
        setSceneEditMessage(
          clean(error?.message) ||
          (isFR ? "Impossible d’enregistrer les modifications." : "Could not save changes.")
        );
      }
    } finally {
      setSceneEditSaving(false);
    }
  }

  function openChapter(chapterId) {
    setActiveChapterId(chapterId);
    setChapterNavigatorId(chapterId);
    setChapterReviewOpen(true);
    setManuscriptWorkspace("chapter");
  }

  // Native Children's Book shortcut: the same saved Chapter and Scene cards,
  // without changing the established Novel/Story chapter-opening contract.
  function openChapterSceneCards(chapterId) {
    openChapter(chapterId);
    setChapterReviewOpen(false);
  }

  function continueChapterWriting(chapter) {
    if (!chapter) return;

    if (
      (!isStructurePreview || isChildrensBridge) &&
      typeof onOpenWriterStudio ===
        "function"
    ) {
      const sceneIds =
        Array.isArray(
          chapter?.sceneIds
        )
          ? chapter.sceneIds
              .map((value) =>
                clean(value)
              )
              .filter(Boolean)
          : [];

      if (sceneIds.length > 0) {
        onOpenWriterStudio({
          chapterId:
            chapter.id,
          sceneId:
            sceneIds[
              sceneIds.length - 1
            ],
        });
      } else {
        onOpenWriterStudio({
          chapterId:
            chapter.id,
          newScene: true,
        });
      }

      return;
    }

    openChapter(
      chapter.id
    );
  }

  async function moveScene(sceneId, targetId) {
    if (isChildrensBridge) return;
    const stripped = chapters.map((chapter) => ({
      ...chapter,
      sceneIds: chapter.sceneIds.filter(
        (id) => id !== sceneId
      ),
    }));

    let nextChapters = stripped;
    let nextUnassigned =
      unassignedSceneIds.filter((id) => id !== sceneId);

    if (targetId === "unassigned") {
      nextUnassigned = [...nextUnassigned, sceneId];
    } else {
      nextChapters = stripped.map((chapter) =>
        chapter.id === targetId
          ? {
              ...chapter,
              sceneIds: [...chapter.sceneIds, sceneId],
            }
          : chapter
      );
    }

    setChapters(nextChapters);
    setUnassignedSceneIds(nextUnassigned);
    if (previewUnassignedSceneId === sceneId) {
      setPreviewUnassignedSceneId("");
    }
    await persistChapters(nextChapters);
  }

  async function moveSceneWithin(
    containerId,
    sceneId,
    direction
  ) {
    if (isChildrensBridge) return;
    if (containerId === "unassigned") {
      const index = unassignedSceneIds.indexOf(sceneId);
      if (index < 0) return;
      const target =
        direction === "up" ? index - 1 : index + 1;
      setUnassignedSceneIds(
        arrayMove(unassignedSceneIds, index, target)
      );
      return;
    }

    const next = chapters.map((chapter) => {
      if (chapter.id !== containerId) return chapter;

      const index = chapter.sceneIds.indexOf(sceneId);
      if (index < 0) return chapter;

      const target =
        direction === "up" ? index - 1 : index + 1;

      return {
        ...chapter,
        sceneIds: arrayMove(
          chapter.sceneIds,
          index,
          target
        ),
      };
    });

    setChapters(next);
    await persistChapters(next);
  }

  async function moveSceneToPosition(
    chapterId,
    sceneId,
    rawPosition
  ) {
    if (isChildrensBridge) return;
    const position = Number(rawPosition);
    if (!Number.isInteger(position)) return;

    const next = chapters.map((chapter) => {
      if (chapter.id !== chapterId) return chapter;

      const index = chapter.sceneIds.indexOf(sceneId);
      const target = position - 1;

      if (
        index < 0 ||
        target < 0 ||
        target >= chapter.sceneIds.length
      ) {
        return chapter;
      }

      return {
        ...chapter,
        sceneIds: arrayMove(
          chapter.sceneIds,
          index,
          target
        ),
      };
    });

    setChapters(next);
    setMoveScenePositionId("");
    await persistChapters(next);
  }

  function addScene(chapterId) {
    if (isChildrensBridge) {
      onChildrensBookAddScene?.(chapterId);
      return;
    }
    if (
      !isStructurePreview &&
      typeof onOpenWriterStudio === "function"
    ) {
      onOpenWriterStudio({
        chapterId,
        newScene: true,
      });
      return;
    }

    const number = nextLocalSceneNumber;
    const id = "local-scene-" + number;

    const scene = {
      _id: id,
      sceneNumber: number,
      title: isFR ? "Nouvelle scène" : "New Scene",
      text: "",
      type: "canon",
      status: "approved",
    };

    setSceneRecords((current) => [
      ...current,
      { id, scene },
    ]);

    setChapters((current) =>
      current.map((chapter) =>
        chapter.id === chapterId
          ? {
              ...chapter,
              sceneIds: [...chapter.sceneIds, id],
            }
          : chapter
      )
    );

    setNextLocalSceneNumber((value) => value + 1);
  }

  function duplicateScene(chapterId, sceneId) {
    if (isChildrensBridge) return;
    const source = sceneById.get(sceneId);
    if (!source) return;

    const number = nextLocalSceneNumber;
    const id = "local-scene-" + number;

    const copy = {
      ...source,
      _id: id,
      id,
      sceneNumber: number,
      title:
        sceneTitle(source, number, isFR) +
        (isFR ? " — copie" : " — Copy"),
      text: "",
    };

    setSceneRecords((current) => [
      ...current,
      { id, scene: copy },
    ]);

    setChapters((current) =>
      current.map((chapter) => {
        if (chapter.id !== chapterId) {
          return chapter;
        }

        const index =
          chapter.sceneIds.indexOf(sceneId);
        const next = chapter.sceneIds.slice();

        next.splice(
          Math.max(0, index + 1),
          0,
          id
        );

        return {
          ...chapter,
          sceneIds: next,
        };
      })
    );

    setNextLocalSceneNumber(
      (value) => value + 1
    );
  }

  async function removeSceneFromChapter(
    chapterId,
    sceneId
  ) {
    if (isChildrensBridge) return;
    const next = chapters.map((chapter) =>
      chapter.id === chapterId
        ? {
            ...chapter,
            sceneIds: chapter.sceneIds.filter(
              (id) => id !== sceneId
            ),
          }
        : chapter
    );

    setChapters(next);
    setUnassignedSceneIds((current) =>
      current.includes(sceneId)
        ? current
        : [...current, sceneId]
    );

    await persistChapters(next);
  }

  function resetDraft() {
    setSceneRecords(initialSceneEntries);
    setNextLocalSceneNumber(
      initialSceneEntries.length + 1
    );
    setEditingChapterId("");
    setMoveChapterPositionId("");
    setMoveScenePositionId("");
    setChapterRangeStart(0);
    setManuscriptWorkspace("book-structure");
    seedStructure();
  }

  async function persistMatter(
    nextFrontMatter = frontMatterItems,
    nextBackMatter = backMatterItems
  ) {
    if (isStructurePreview) {
      setMatterMessage(
        isFR
          ? "Aperçu uniquement · aucune sauvegarde"
          : "Preview only · nothing saved"
      );
      return true;
    }

    if (!storyId) return false;

    setSavingMatter(true);
    setMatterMessage(
      isFR ? "Enregistrement…" : "Saving…"
    );

    try {
      const response = await api(
        `/api/stories/${encodeURIComponent(storyId)}/matter`,
        {
          method: "PATCH",
          body: JSON.stringify({
            frontMatter: nextFrontMatter,
            backMatter: nextBackMatter,
          }),
        }
      );

      if (response?.story) {
        setStory(response.story);
      }

      setMatterMessage(
        isFR ? "Éléments enregistrés" : "Matter saved"
      );

      return true;
    } catch (err) {
      setMatterMessage(
        clean(err?.message) ||
          (isFR
            ? "Impossible d’enregistrer ces éléments."
            : "Could not save Front and Back Matter.")
      );
      return false;
    } finally {
      setSavingMatter(false);
    }
  }

  function matterItemsFor(kind) {
    return kind === "front"
      ? frontMatterItems
      : backMatterItems;
  }

  function setMatterItemsFor(kind, updater) {
    if (kind === "front") {
      setFrontMatterItems(updater);
      return;
    }

    setBackMatterItems(updater);
  }

  async function addMatterItem(kind) {
    if (isChildrensBridge) return;
    const front = kind === "front";
    const number = front
      ? nextFrontMatterNumber
      : nextBackMatterNumber;
    const id =
      (front ? "front-matter-" : "back-matter-") +
      Date.now().toString(36) +
      "-" +
      number;

    const item = {
      id,
      title: front
        ? (
            isFR
              ? "Pages liminaires " + number
              : "Front Matter " + number
          )
        : (
            isFR
              ? "Élément de fin " + number
              : "Back Matter " + number
          ),
      text: "",
      manuscriptContent: null,
    };

    const nextFront = front
      ? [...frontMatterItems, item]
      : frontMatterItems;
    const nextBack = front
      ? backMatterItems
      : [...backMatterItems, item];

    if (front) {
      setFrontMatterItems(nextFront);
      setNextFrontMatterNumber(
        (value) => value + 1
      );
    } else {
      setBackMatterItems(nextBack);
      setNextBackMatterNumber(
        (value) => value + 1
      );
    }

    await persistMatter(nextFront, nextBack);
  }

  function openMatterTemplate(kind, template) {
    if (isChildrensBridge) return;
    pendingMatterEditRef.current = null;
    const existing = existingNovelMatterTemplate(matterItemsFor(kind), template);
    if (existing) {
      setMatterEditorKind(kind);
      setMatterEditorItemId(existing.id);
      return;
    }
    if (isStructurePreview || savingMatter) return;
    // Do not persist an empty template: the backend deliberately rejects
    // empty Matter, and review/PDF must contain saved user content only.
    const id = (kind === "front" ? "front-matter-" : "back-matter-") +
      "template-" + Date.now().toString(36);
    setMatterTemplateDraft({
      kind,
      item: { id, title: template.title, text: "", manuscriptContent: null },
    });
    setMatterMessage("");
    setMatterEditorKind(kind);
    setMatterEditorItemId(id);
  }

  function cancelMatterTemplate() {
    setMatterHeaderNotice("");
    pendingMatterEditRef.current = null;
    setMatterTemplateDraft(null);
    setMatterEditorKind("");
    setMatterEditorItemId("");
    setMatterMessage("");
  }

  function openPrintedAuthorSetupFromMatter() {
    // This is a navigation action only: never quietly discard local edits.
    if (isStructurePreview || savingMatter) return;
    if (pendingMatterEditRef.current?.kind === matterEditorKind &&
        pendingMatterEditRef.current?.id === matterEditorItemId) {
      setMatterHeaderNotice(isFR
        ? "Enregistrez ou annulez vos modifications du sous-titre avant d’ouvrir Book Setup."
        : "Save or Cancel your Subtitle edits before opening Book Setup.");
      return;
    }
    cancelMatterTemplate();
    navigateBookWorkspace("setup");
    beginBookSetupEdit();
  }

  async function completeMatterEditing() {
    if (isChildrensBridge) return;
    const kind = matterEditorKind;
    const id = matterEditorItemId;
    const pending = pendingMatterEditRef.current;
    const patch = pending?.kind === kind && pending?.id === id
      ? pending.patch : null;
    const template = matterTemplateDraft?.kind === kind &&
      matterTemplateDraft?.item?.id === id
      ? matterTemplateDraft.item : null;

    if (template) {
      const item = patch ? { ...template, ...patch } : template;
      if (!hasReadableNovelMatterBody(item)) {
        setMatterMessage(isFR
          ? "Ajoutez du texte avant d’enregistrer cette rubrique facultative."
          : "Add text before saving this optional section.");
        return;
      }
      const nextFront = kind === "front"
        ? [...frontMatterItems, item] : frontMatterItems;
      const nextBack = kind === "back"
        ? [...backMatterItems, item] : backMatterItems;
      const ok = await persistMatter(nextFront, nextBack);
      if (!ok) return;
      setFrontMatterItems(nextFront);
      setBackMatterItems(nextBack);
      setMatterTemplateDraft(null);
    } else {
      // Apply only the specific section's pending rich text, preserving every
      // other saved Front and Back Matter entry, order, title and identity.
      const nextFront = kind === "front"
        ? frontMatterItems.map(item => item.id === id && patch
          ? { ...item, ...patch } : item) : frontMatterItems;
      const nextBack = kind === "back"
        ? backMatterItems.map(item => item.id === id && patch
          ? { ...item, ...patch } : item) : backMatterItems;
      if (!isStructurePreview) {
        const ok = await persistMatter(nextFront, nextBack);
        if (!ok) return; // Keep the editor and unsaved text open on failure.
      }
      setFrontMatterItems(nextFront);
      setBackMatterItems(nextBack);
    }
    pendingMatterEditRef.current = null;
    setMatterEditorKind("");
    setMatterEditorItemId("");
  }

  // A reusable optional library for both Book Structure and each Matter tab.
  // Cards are NOT manuscript content, and unused templates do not create pages.
  function renderMatterTemplateCatalog(kind) {
    const front = kind === "front";
    const templates = front ? NOVEL_FRONT_MATTER_TEMPLATES : NOVEL_BACK_MATTER_TEMPLATES;
    const savedItems = matterItemsFor(kind);
    return (
      <section className="ns-card" data-novel-story-matter-template-catalog={kind}
        style={{ padding: 18, borderRadius: 18,
          border: "1px solid rgba(148,163,184,0.32)", background: "#fafafa" }}>
        <div style={{ display: "flex", gap: 10,
          justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
          <strong>{front
            ? (isFR ? "Rubriques habituelles · Préliminaires" : "Standard Front Matter")
            : (isFR ? "Rubriques habituelles · Annexes" : "Standard Back Matter")}</strong>
          <span className="ns-card-meta">{isFR
            ? "Facultatif · seuls les textes enregistrés figurent dans le livre"
            : "Optional · only saved writing appears in review and PDF"}</span>
        </div>
        <div style={{ display: "grid", gap: 8, marginTop: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
          {templates.map((template) => {
            const existing = existingNovelMatterTemplate(savedItems, template);
            return (
              <div key={template.title} data-novel-story-matter-template={kind}
                style={{ padding: "10px 12px", borderRadius: 12,
                  background: "#ffffff", border: "1px solid rgba(148,163,184,0.26)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 13, display: "block" }}>
                    {isFR ? template.fr : template.title}
                  </strong>
                  <span className="ns-card-meta" style={{ fontSize: 11 }}>
                    {existing ? (isFR ? "Dans le livre" : "In book") :
                      (isFR ? "Non utilisé" : "Unused")}
                  </span>
                </div>
                <button type="button" className="ns-button ns-button-outline"
                  style={{ padding: "6px 9px", fontSize: 12, flexShrink: 0 }}
                  disabled={savingMatter || isStructurePreview}
                  data-novel-story-open-matter-template={kind}
                  onClick={() => openMatterTemplate(kind, template)}>
                  {existing ? (isFR ? "Modifier" : "Edit") :
                    (isFR ? "Rédiger" : "Add Content")}
                </button>
              </div>
            );
          })}
        </div>
        <p className="ns-card-meta" style={{ margin: "10px 0 0" }}>
          {isFR
            ? "La table des matières et l’index ne sont pas générés automatiquement. Ajoutez leur texte si vous souhaitez les utiliser."
            : "Table of Contents and Index are author-written here; automatic generation is not enabled."}
        </p>
      </section>
    );
  }

  function updateMatterItem(kind, itemId, patch) {
    if (isChildrensBridge) return;
    setMatterItemsFor(
      kind,
      (current) =>
        current.map((item) =>
          item.id === itemId
            ? { ...item, ...(patch || {}) }
            : item
        )
    );
  }

  async function removeMatterItem(kind, itemId) {
    if (isChildrensBridge) return;
    const nextFront =
      kind === "front"
        ? frontMatterItems.filter(
            (item) => item.id !== itemId
          )
        : frontMatterItems;
    const nextBack =
      kind === "back"
        ? backMatterItems.filter(
            (item) => item.id !== itemId
          )
        : backMatterItems;

    setFrontMatterItems(nextFront);
    setBackMatterItems(nextBack);

    if (
      matterEditorKind === kind &&
      matterEditorItemId === itemId
    ) {
      setMatterEditorKind("");
      setMatterEditorItemId("");
    }

    await persistMatter(nextFront, nextBack);
  }

  // Front Matter and Back Matter are independent reading surfaces:
  // each displays only its own saved items in their stored order.
  function renderMatterReview(kind) {
    const front = kind === "front";
    const items = matterItemsFor(kind);
    const label = front
      ? (isFR ? "Préliminaires" : "Front Matter")
      : (isFR ? "Postliminaires" : "Back Matter");
    return (
      <div
        data-novel-story-matter-review={kind}
        aria-label={isFR ? `Lire / réviser : ${label}` : `Read / Review: ${label}`}
        style={{ display: "grid", gap: 14, marginTop: 16 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
            padding: "12px 14px",
            background: "rgba(248,250,252,0.9)",
            border: "1px solid rgba(148,163,184,0.24)",
            borderRadius: 12,
          }}
        >
          <div>
            <strong>{isFR ? "LECTURE DU LIVRE · " : "BOOK READING · "}{label}</strong>
            <div className="ns-card-meta" style={{ marginTop: 5 }}>
              {items.length} {isFR ? "éléments" : "items"} ·{" "}
              {items.reduce((total, item) => total + wordCount(item.text), 0).toLocaleString()}{" "}
              {isFR ? "mots" : "words"}
            </div>
          </div>
          <span className="ns-card-meta">
            {isFR
              ? "Lecture seule · le contenu enregistré et sa mise en forme"
              : "Read-only · saved content and formatting"}
          </span>
        </div>

        {items.length > 0 ? (
          <div
            data-novel-story-matter-reading-surface={kind}
            style={{
              display: "grid",
              gap: 0,
              padding: "clamp(16px, 3vw, 34px)",
              background: "#fff",
              borderRadius: 16,
              border: "1px solid rgba(148,163,184,0.3)",
              overflowWrap: "anywhere",
              lineHeight: 1.78,
            }}
          >
            {items.map((item, index) => (
              <article
                key={item.id}
                data-novel-story-matter-review-item={kind}
                style={{
                  paddingBottom: 22,
                  marginBottom: index < items.length - 1 ? 26 : 0,
                  borderBottom: index < items.length - 1
                    ? "1px solid rgba(148,163,184,0.30)" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "start",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 18,
                  }}
                >
                  <div>
                    <div
                      className="ns-card-meta"
                      style={{ fontWeight: 800, letterSpacing: "0.08em" }}
                    >
                      {isFR ? "ÉLÉMENT " : "ITEM "}{index + 1} / {items.length}
                    </div>
                    <h3 style={{ margin: "5px 0", fontSize: 22 }}>
                      {clean(item.title)}
                    </h3>
                  </div>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    data-novel-story-matter-review-edit={kind}
                    onClick={() => {
                      setMatterEditorKind(kind);
                      setMatterEditorItemId(item.id);
                    }}
                  >
                    {isFR ? "Modifier le contenu" : "Edit Content"}
                  </button>
                </div>
                <div
                  data-novel-story-matter-review-text={kind}
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: "1.02rem",
                    minHeight: 36,
                  }}
                >
                  <ChildrensBookManuscriptReadOnly
                    content={item.manuscriptContent || null}
                    fallbackText={String(item.text || "")}
                    lang={isFR ? "fr" : "en"}
                  />
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p
            className="ns-card-meta"
            data-novel-story-matter-review-empty={kind}
          >
            {isFR
              ? "Aucun élément enregistré. Revenez à la gestion pour ajouter du contenu."
              : "No saved items yet. Return to Manage Items to add content."}
          </p>
        )}
      </div>
    );
  }

  function renderMatterSection(kind) {
    const front = kind === "front";
    if (isChildrensBridge) {
      // Children's Book owns logical Matter via its Blueprint, NOT Novel's APIs.
      // The real Novel workspace tabs are retained; only the editor is bridged.
      if (typeof onChildrensBookRenderMatter === 'function')
        return onChildrensBookRenderMatter(kind);
      return <p role="alert">Children's Book Matter editor unavailable.</p>;
    }
    const rows = matterItemsFor(kind);
    const reading = matterReviewKind === kind;

    return (
      <section
        className="ns-card"
        data-novel-story-matter-area={kind}
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: "0 0 8px" }}>
              {front
                ? (
                    isFR
                      ? "Préliminaires"
                      : "Front Matter"
                  )
                : (
                    isFR
                      ? "Postliminaires"
                      : "Back Matter"
                  )}
            </h3>

            <p
              className="ns-card-meta"
              style={{ margin: 0 }}
            >
              {front
                ? (
                    isFR
                      ? "Page de titre, droits d’auteur, dédicace, remerciements, préface et autres éléments choisis par l’auteur."
                      : "Title Page, Copyright, Dedication, Acknowledgements, Preface and other writer-selected front matter."
                  )
                : (
                    isFR
                      ? "Notes de l’auteur, À propos de l’auteur, remerciements finaux et autres éléments de fin de livre."
                      : "Author Notes, About the Author, final acknowledgements and other end-of-book material."
                  )}
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className={reading ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
              type="button"
              data-novel-story-matter-review-toggle={kind}
              aria-pressed={reading}
              onClick={() => setMatterReviewKind(reading ? "" : kind)}
            >
              {reading
                ? (isFR ? "← Gérer les éléments" : "← Manage Items")
                : (front
                    ? (isFR ? "Lire / réviser les préliminaires" : "Read / Review Front Matter")
                    : (isFR ? "Lire / réviser les postliminaires" : "Read / Review Back Matter"))}
            </button>
            {!reading ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            data-novel-story-add-matter={kind}
            onClick={() => addMatterItem(kind)}
            disabled={savingMatter}
          >
            {front
              ? (
                  isFR
                    ? "+ Ajouter des pages liminaires"
                    : "+ Add Front Matter"
                )
              : (
                  isFR
                    ? "+ Ajouter un élément de fin"
                    : "+ Add Back Matter"
                )}
          </button>
            ) : null}
          </div>
        </div>

        {!reading ? (
          story?.bookCategory === "children_book" && !isChildrensBridge ? (
            <details data-native-childrens-optional-matter-v1f2f={kind}
              style={{ marginTop: 14, border: "1px solid #d1e5dd",
                borderRadius: 14, padding: "12px 16px", background: "#f9fffc" }}>
              <summary style={{ fontWeight: 800, cursor: "pointer", color: "#065f46" }}>
                {isFR ? "Ajouter une section facultative" : "Add an optional section"}
              </summary>
              <div style={{ marginTop: 12 }}>{renderMatterTemplateCatalog(kind)}</div>
            </details>
          ) : renderMatterTemplateCatalog(kind)
        ) : null}

        {reading ? renderMatterReview(kind) : (
        <div
          style={{
            marginTop: 14,
            display: "grid",
            gap: 10,
          }}
        >
            {rows.length > 0 ? (
              rows.map((item) => (
                <div
                  key={item.id}
                  data-novel-story-matter-card={kind}
                  style={{
                    border:
                      "1px solid rgba(100,116,139,0.24)",
                    borderRadius: 14,
                    padding: 14,
                    background: "#ffffff",
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <span>
                      {isFR ? "Titre" : "Title"}
                    </span>
                    <input
                      className="ns-input"
                      value={item.title}
                      onChange={(event) =>
                        updateMatterItem(
                          kind,
                          item.id,
                          {
                            title:
                              event.target.value,
                          }
                        )
                      }
                      onBlur={() =>
                        persistMatter(
                          frontMatterItems,
                          backMatterItems
                        )
                      }
                    />
                  </label>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() => {
                        setMatterEditorKind(kind);
                        setMatterEditorItemId(
                          item.id
                        );
                      }}
                    >
                      {isFR
                        ? "Modifier le contenu"
                        : "Edit Content"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      data-novel-story-request-remove-matter={kind}
                      disabled={savingMatter}
                      onClick={() =>
                        setPendingMatterRemoval({
                          kind,
                          itemId: item.id,
                        })
                      }
                    >
                      {isFR
                        ? "Supprimer"
                        : "Remove"}
                    </button>

                    <span className="ns-card-meta">
                      {wordCount(item.text)}{" "}
                      {isFR ? "mots" : "words"}
                      {" · "}
                      {isStructurePreview
                        ? (
                            isFR
                              ? "aperçu uniquement"
                              : "preview only"
                          )
                        : (
                            savingMatter
                              ? (
                                  isFR
                                    ? "enregistrement…"
                                    : "saving…"
                                )
                              : (
                                  matterMessage ||
                                  (isFR
                                    ? "enregistré"
                                    : "saved")
                                )
                          )}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: 14,
                  borderRadius: 14,
                  border:
                    "1px dashed rgba(100,116,139,0.34)",
                  background:
                    "rgba(255,255,255,0.76)",
                }}
              >
                <span className="ns-card-meta">
                  {isFR
                    ? "Rien ici pour le moment. Utilisez Ajouter pour créer un élément."
                    : "Nothing here yet. Use Add to create an item."}
                </span>
              </div>
            )}
        </div>
        )}
      </section>
    );
  }

  function renderSceneEditor() {
    const draft = editingSceneDraft;
    if (!draft) return null;

    const canonLocked = Boolean(
      story?.proposalsOpen &&
      !clean(story?.writingMode).toLowerCase().includes("solo")
    );
    return (
      <div
        data-novel-story-scene-editor-backdrop="true"
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1450,
          display: "grid",
          placeItems: "center",
          padding: 12,
          background: "rgba(15,23,42,0.54)",
        }}
      >
        <div
          data-novel-story-scene-editor="true"
          role="dialog"
          aria-modal="true"
          aria-label={isFR ? "Modifier la scène Canon" : "Edit Canon Scene"}
          style={{
            width: "min(1030px, 96vw)",
            maxHeight: "92vh",
            overflowY: "auto",
            padding: "clamp(14px, 2vw, 24px)",
            borderRadius: 20,
            background: "#f0fdf4",
            border: "1px solid rgba(5,150,105,0.32)",
            boxShadow: "0 28px 84px rgba(15,23,42,0.34)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>
              {isFR ? "Modifier la scène Canon" : "Edit Canon Scene"}
            </h3>
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={closeSceneEditor}
              disabled={sceneEditSaving}
            >
              {isFR ? "Fermer" : "Close"}
            </button>
          </div>
          <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
            {isFR
              ? "Vous modifiez la scène Canon sélectionnée. Son identité et son historique sont préservés."
              : "You are editing this specific saved Canon Scene. Its identity and history are preserved."}
          </p>

          <label style={{ display: "grid", gap: 6, marginBottom: 14 }}>
            <strong>{isFR ? "Titre de la scène" : "Scene title"}</strong>
            <input
              className="ns-input"
              value={draft.title}
              maxLength={200}
              disabled={sceneEditSaving || canonLocked}
              onChange={(event) =>
                setEditingSceneDraft((current) => current
                  ? { ...current, title: event.target.value, dirty: true }
                  : current
                )
              }
            />
          </label>

          <NovelStorySceneRichEditor
            id={draft.sceneId}
            title={draft.title}
            text={draft.text}
            manuscriptContent={draft.manuscriptContent}
            disabled={sceneEditSaving || canonLocked}
            lang={isFR ? "fr" : "en"}
            saveActionLabel={isFR ? "Enregistrer la scène" : "Save Scene"}
            onChange={(value) =>
              setEditingSceneDraft((current) => current
                ? {
                    ...current,
                    text: String(value?.text || ""),
                    manuscriptContent: value?.manuscriptContent || null,
                    dirty: true,
                  }
                : current
              )
            }
          />

          <label style={{ display: "grid", gap: 6, marginTop: 16 }}>
            <strong>{isFR ? "Affectation au chapitre" : "Chapter assignment"}</strong>
            <select
              className="ns-input"
              data-novel-story-scene-editor-chapter="true"
              value={draft.targetChapterId}
              disabled={sceneEditSaving || canonLocked}
              onChange={(event) =>
                setEditingSceneDraft((current) => current
                  ? { ...current, targetChapterId: event.target.value, dirty: true }
                  : current
                )
              }
            >
              <option value="">
                {isFR ? "Scène non affectée" : "Unassigned Scene"}
              </option>
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {clean(chapter.title)}
                </option>
              ))}
            </select>
          </label>

          {canonLocked ? (
            <p role="status" className="ns-card-meta">
              {isFR
                ? "Canon est verrouillé pendant le tour de propositions."
                : "Canon is locked while a Proposal Round is open."}
            </p>
          ) : isStructurePreview ? (
            <p role="status" className="ns-card-meta">
              {isFR ? "Aperçu seulement : aucune modification enregistrée." : "Preview only: no changes are saved."}
            </p>
          ) : null}

          {sceneEditMessage ? (
            <p
              role="status"
              data-novel-story-scene-editor-message="true"
              style={{
                marginTop: 14,
                padding: 10,
                border: "1px solid rgba(148,163,184,0.36)",
                borderRadius: 10,
              }}
            >
              {sceneEditMessage}
            </p>
          ) : null}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 16,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={sceneEditSaving}
              onClick={closeSceneEditor}
            >
              {isFR ? "Annuler" : "Cancel"}
            </button>
            <button
              className="ns-button ns-button-primary"
              type="button"
              data-novel-story-scene-editor-save="true"
              disabled={sceneEditSaving || canonLocked || isStructurePreview}
              onClick={saveEditedScene}
            >
              {sceneEditSaving
                ? (isFR ? "Enregistrement…" : "Saving…")
                : (isFR ? "Enregistrer la scène et le chapitre" : "Save Scene & Chapter")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderMatterEditor() {
    if (
      !matterEditorKind ||
      !matterEditorItemId
    ) {
      return null;
    }

    const isNewTemplate = Boolean(
      matterTemplateDraft?.kind === matterEditorKind &&
      matterTemplateDraft?.item?.id === matterEditorItemId
    );
    const item = isNewTemplate
      ? matterTemplateDraft.item
      : matterItemsFor(matterEditorKind).find(
          (candidate) => candidate.id === matterEditorItemId
        );

    if (!item) return null;

    const front =
      matterEditorKind === "front";
    const isSubtitleMatter = front &&
      /^(subtitle|sub-title|sous-titre)$/i.test(clean(item.title));

    return (
      <div
        data-novel-story-floating-matter-editor-backdrop="true"
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1400,
          display: "grid",
          placeItems: "center",
          padding: "clamp(8px, 2vw, 20px)",
          overflow: "hidden",
          background:
            "rgba(15,23,42,0.52)",
        }}
      >
        <div
          data-novel-story-floating-matter-editor="true"
          role="dialog"
          aria-modal="true"
          aria-label={front
            ? (isFR ? "Modifier les pages liminaires" : "Edit Front Matter")
            : (isFR ? "Modifier les éléments de fin" : "Edit Back Matter")}
          style={{
            width: "min(1100px, 96vw)",
            height: "min(92dvh, 960px)",
            maxHeight: "calc(100dvh - 16px)",
            minHeight: 0,
            overflow: "hidden",
            boxSizing: "border-box",
            padding: 0,
            borderRadius: 20,
            border:
              "1px solid rgba(5,150,105,0.30)",
            background: "#ffffff",
            boxShadow:
              "0 30px 90px rgba(15,23,42,0.32)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            data-novel-story-matter-editor-header="true"
            style={{
              flex: "0 0 auto",
              padding: "14px 20px",
              background: "#f7fdf9",
              borderBottom: "1px solid rgba(148,163,184,0.24)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#047857",
              }}
            >
              {front
                ? (
                    isFR
                      ? "PAGES LIMINAIRES"
                      : "FRONT MATTER"
                  )
                : (
                    isFR
                      ? "ÉLÉMENTS DE FIN"
                      : "BACK MATTER"
                  )}
            </div>

            <h4
              style={{
                margin: "5px 0 4px",
                fontSize: 24,
              }}
            >
              {item.title}
            </h4>

            <p
              className="ns-card-meta"
              style={{ margin: 0 }}
            >
              {isStructurePreview
                ? (
                    isFR
                      ? "Aperçu uniquement : vous pouvez rédiger et mettre en forme ici, mais rien ne sera sauvegardé."
                      : "Preview only: write and format this content here, but nothing will be saved."
                  )
                : (
                    isFR
                      ? "Rédigez et mettez en forme cet élément. Terminer l’édition enregistre le contenu."
                      : "Write and format this item. Done Editing saves the content."
                  )}
            </p>
          {/* The PDF byline lives outside the rich-text editor, but remains
              visible in the fixed header even while the text pane scrolls. */}
          {isSubtitleMatter ? (
            <aside data-novel-story-matter-saved-author-byline="true"
              style={{ display: "flex", alignItems: "center", gap: 12,
                flexWrap: "wrap", padding: "9px 12px", marginTop: 10,
                background: "#f0f9ff", border: "1px solid #bfdbfe",
                borderRadius: 12 }}>
              <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                <strong style={{ display: "block", color: "#1e40af",
                  fontSize: 12 }}>
                  {isFR ? "Nom d’auteur imprimé dans le PDF" : "Printed author byline in PDF"}
                </strong>
                <span data-novel-story-saved-author-byline="true"
                  style={{ display: "block", marginTop: 2, fontWeight: 750 }}>
                  {clean(story?.publishedAuthorName || story?.authorName) ||
                    (isFR ? "Aucun nom enregistré" : "No saved author byline")}
                </span>
                <small className="ns-card-meta">
                  {isFR
                    ? "Ajouté automatiquement depuis Book Setup, séparément du texte du sous-titre."
                    : "Added automatically from Book Setup, separately from the Subtitle text below."}
                </small>
              </div>
              <button type="button" className="ns-button ns-button-outline"
                data-novel-story-edit-printed-author-from-subtitle="true"
                disabled={savingMatter || isStructurePreview}
                onClick={openPrintedAuthorSetupFromMatter}>
                {isFR ? "Modifier dans Book Setup" : "Edit in Book Setup"}
              </button>
            </aside>
          ) : null}
          {isSubtitleMatter && matterHeaderNotice ? (
            <p data-novel-story-matter-author-navigation-notice="true"
              role="status" style={{ margin: "6px 0 0", fontSize: 12,
                color: "#9a3412", fontWeight: 700 }}>
              {matterHeaderNotice}
            </p>
          ) : null}
          </div>

          {/* Exactly one scroll container, unlike the previous nested modal +
              62vh editor surface. The formatting toolbar stays sticky inside. */}
          <div
            data-novel-story-matter-editor-scroll="true"
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              scrollBehavior: "auto",
              scrollbarGutter: "stable",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              padding: "16px 20px 24px",
            }}
          >
          <ChildrensBookManuscriptEditor
            lang={isFR ? "fr" : "en"}
            entry={matterEditorInitialEntry}
            compactToolbar={true}
            stickyToolbar={true}
            enableWholeDocumentJustify={true}
            boundedSurface={false}
            saveActionLabel={
              isStructurePreview
                ? (
                    isFR
                      ? "Aperçu — non enregistré"
                      : "Preview — Not Saved"
                  )
                : (
                    isFR
                      ? "Enregistrer l’élément"
                      : "Save Matter"
                  )
            }
            onChange={(value) => {
              // No parent setState while typing; keep scroll and caret stable.
              pendingMatterEditRef.current = {
                kind: matterEditorKind,
                id: item.id,
                patch: {
                  manuscriptContent: value?.manuscriptContent || null,
                  text: String(value?.manuscriptText || ""),
                },
              };
            }}
          />
          </div>

          <div
            data-novel-story-matter-editor-footer="true"
            style={{
              flex: "0 0 auto",
              padding: "12px 20px",
              borderTop: "1px solid rgba(148,163,184,0.26)",
              background: "#ffffff",
            }}
          >
          {isNewTemplate && matterMessage ? (
            <p role="status" style={{ color: "#9a3412", margin: 0 }}>
              {matterMessage}
            </p>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button className="ns-button ns-button-outline"
              type="button" onClick={cancelMatterTemplate}
              disabled={savingMatter}>
              {isFR ? "Annuler" : "Cancel"}
            </button>
            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={savingMatter}
              onClick={completeMatterEditing}
            >
              {isFR
                ? "Terminer"
                : "Done Editing"}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  function focusStructureChapter(chapterId) {
    const id = clean(chapterId);
    if (!id) return;

    setChapterNavigatorId(id);

    if (typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target =
          document.getElementById(
            "novel-story-structure-" + id
          );

        target?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
      });
    });
  }

  function chapterNavigator() {
    const index = chapters.findIndex(
      (chapter) =>
        chapter.id === chapterNavigatorId
    );

    return (
      <div
        data-novel-story-chapter-navigator="true"
        style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 16,
          border:
            "1px solid rgba(16,185,129,0.26)",
          background:
            "rgba(248,250,252,0.92)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#047857",
              }}
            >
              {isFR
                ? "NAVIGATEUR DE CHAPITRES"
                : "CHAPTER NAVIGATOR"}
            </div>
            <strong>
              {chapters.length}{" "}
              {isFR
                ? (chapters.length === 1 ? "chapitre dans ce livre" : "chapitres dans ce livre")
                : (chapters.length === 1 ? "chapter in this book" : "chapters in this book")}
            </strong>
          </div>

          <span className="ns-card-meta">
            {isFR
              ? "Choisissez un chapitre pour l’amener directement au centre de l’écran."
              : "Choose a Chapter to bring it directly into view."}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "auto minmax(220px, 1fr) auto",
            gap: 10,
            alignItems: "end",
            marginTop: 14,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={index <= 0}
            onClick={() =>
              focusStructureChapter(
                chapters[index - 1]?.id || ""
              )
            }
          >
            {isFR
              ? "Chapitre précédent"
              : "Previous Chapter"}
          </button>

          <label
            style={{
              display: "grid",
              gap: 6,
              fontWeight: 800,
            }}
          >
            <span>
              {isFR
                ? "Aller au chapitre"
                : "Jump to Chapter"}
            </span>
            <select
              className="ns-input"
              value={chapterNavigatorId}
              onChange={(event) =>
                focusStructureChapter(
                  event.target.value
                )
              }
            >
              {chapters.map(
                (chapter, chapterIndex) => (
                  <option
                    key={chapter.id}
                    value={chapter.id}
                  >
                    {clean(chapter.title) ||
                      ((isFR
                        ? "Chapitre "
                        : "Chapter ") +
                        (chapterIndex + 1))}
                  </option>
                )
              )}
            </select>
          </label>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              index < 0 ||
              index >= chapters.length - 1
            }
            onClick={() =>
              focusStructureChapter(
                chapters[index + 1]?.id || ""
              )
            }
          >
            {isFR
              ? "Chapitre suivant"
              : "Next Chapter"}
          </button>
        </div>
      </div>
    );
  }

  // Both Book Structure and the Chapter view use the same proven
  // owner assignment control and persisted moveScene() operation.
  function renderUnassignedSceneRows(sceneIds = unassignedSceneIds, compact = false) {
    return (
              <div
                style={{
                  display: "grid",
                  gap: 10,
                  marginTop: 12,
                }}
              >
                {sceneIds.map(
                  (
                    sceneId,
                    sceneIndex
                  ) => {
                    const scene =
                      sceneById.get(sceneId);

                    if (!scene) {
                      return null;
                    }

                    return (
                      <div
                        key={sceneId}
                        style={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          gap: 10,
                          alignItems:
                            "center",
                          flexWrap: "wrap",
                          padding: 12,
                          borderRadius: 12,
                          border:
                            "1px solid rgba(148,163,184,0.28)",
                        }}
                      >
                        <strong style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                          {sceneTitle(
                            scene,
                            sceneIndex + 1,
                            isFR
                          )}
                        </strong>

                        <select
                          className="ns-input"
                          defaultValue=""
                          onChange={(event) => {
                            const target =
                              event.target
                                .value;
                            if (!target) {
                              return;
                            }
                            moveScene(
                              sceneId,
                              target
                            );
                          }}
                          style={{
                            width: "auto",
                            minWidth: 190,
                          }}
                        >
                          <option value="">
                            {isFR
                              ? "Déplacer vers un chapitre…"
                              : "Move to Chapter…"}
                          </option>
                          {chapters.map(
                            (chapter) => (
                              <option
                                key={
                                  chapter.id
                                }
                                value={
                                  chapter.id
                                }
                              >
                                {clean(
                                  chapter.title
                                )}
                              </option>
                            )
                          )}
                        </select>

                        {compact ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              data-novel-story-unassigned-preview-action="true"
                              aria-expanded={previewUnassignedSceneId === sceneId}
                              onClick={() =>
                                setPreviewUnassignedSceneId(
                                  previewUnassignedSceneId === sceneId ? "" : sceneId
                                )
                              }
                            >
                              {previewUnassignedSceneId === sceneId
                                ? (isFR ? "Masquer" : "Hide")
                                : (isFR ? "Lire la scène" : "View Scene")}
                            </button>
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              data-novel-story-unassigned-open-editor="true"
                              onClick={() => openSceneForEditing(sceneId)}
                            >
                              {isFR ? "Modifier la scène" : "Edit Scene"}
                            </button>
                          </div>
                        ) : null}

                        {compact && previewUnassignedSceneId === sceneId ? (
                          <div
                            data-novel-story-unassigned-scene-preview="true"
                            style={{
                              flexBasis: "100%",
                              padding: 14,
                              borderRadius: 12,
                              background: "rgba(248,250,252,0.94)",
                              border: "1px solid rgba(148,163,184,0.3)",
                            }}
                          >
                            <strong>
                              {isFR ? "Texte de la scène · aperçu" : "Scene text · preview"}
                            </strong>
                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere",
                                overflowY: "auto",
                                maxHeight: 260,
                                lineHeight: 1.65,
                                marginTop: 10,
                              }}
                            >
                              {plainText(sceneText(scene)) ||
                                (isFR ? "Aucun texte pour cette scène." : "This Scene has no text yet.")}
                            </div>
                            <p className="ns-card-meta" style={{ marginBottom: 0 }}>
                              {isFR
                                ? "L’aperçu est en lecture seule. Pour modifier le texte enrichi et l’enregistrer, ouvrez Writer Studio puis Modifier la scène."
                                : "This preview is read-only. Open Writer Studio and choose Edit Scene to change and save the rich text."}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                )}
              </div>
    );
  }

  // Owner-only Book Workspace reading pass, separate from Writer Studio.
  // The manuscript includes only assigned Canon: unassigned scenes are visible
  // in a separate, clearly excluded review shelf after Back Matter.
  function renderWholeBookReview() {
    const frontItems = frontMatterItems;
    const backItems = backMatterItems;
    const assignedCount = chapters.reduce(
      (sum, chapter) => sum + safeArray(chapter.sceneIds).length, 0
    );
    const reviewSections = [
      ...(frontItems.length > 0
        ? [{ id: "front", label: isFR ? "Préliminaires" : "Front Matter" }]
        : []),
      ...chapters.map((chapter, index) => ({
        id: `chapter:${chapter.id}`,
        label: `${isFR ? "Chapitre" : "Chapter"} ${index + 1} · ${clean(chapter.title) || (isFR ? "Sans titre" : "Untitled")}`,
      })),
      ...(backItems.length > 0
        ? [{ id: "back", label: isFR ? "Postliminaires" : "Back Matter" }]
        : []),
      ...(unassignedSceneIds.length > 0
        ? [{ id: "unassigned", label: isFR ? "Scènes non affectées · hors du livre" : "Unassigned Scenes · outside the book" }]
        : []),
    ];
    const firstReviewSection = reviewSections[0]?.id || "all";
    const activeReviewSection =
      wholeBookReviewSection === "all" ||
      reviewSections.some((section) => section.id === wholeBookReviewSection)
        ? wholeBookReviewSection
        : firstReviewSection;
    const showWholeBookAll = activeReviewSection === "all";
    const reviewSectionIndex = reviewSections.findIndex(
      (section) => section.id === activeReviewSection
    );

    function navigateWholeBookReview(sectionId) {
      setWholeBookReviewSection(sectionId);
      if (typeof document !== "undefined") {
        const surface = document.querySelector('[data-novel-story-whole-book-review="true"]');
        if (surface && typeof surface.scrollIntoView === "function") {
          surface.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }

    function renderWholeBookNavigator(position = "top") {
      return (
        <nav
          data-novel-story-whole-book-navigator={position}
          aria-label={isFR ? "Parcourir les sections du livre" : "Navigate book sections"}
          style={{
            padding: "14px 16px",
            borderRadius: 16,
            border: "1px solid rgba(37,99,235,0.25)",
            borderLeft: "5px solid #2563eb",
            background: "#eff6ff",
            display: "flex",
            alignItems: "end",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <label style={{ flex: "1 1 300px", display: "grid", gap: 5 }}>
            <span className="ns-card-meta" style={{ fontWeight: 850 }}>
              {isFR ? "Aller à une section" : "Jump to book section"}
            </span>
            <select
              className="ns-input"
              data-novel-story-whole-book-section-select={position}
              aria-label={isFR ? "Choisir une section du livre" : "Choose book section"}
              value={activeReviewSection}
              onChange={(event) => navigateWholeBookReview(event.target.value)}
              style={{ width: "100%" }}
            >
              {reviewSections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                </option>
              ))}
              <option value="all">{isFR ? "Lire tout le livre d’un seul tenant" : "Read All — continuous manuscript"}</option>
            </select>
          </label>
          <button
            className="ns-button ns-button-outline"
            type="button"
            data-novel-story-whole-book-previous={position}
            disabled={showWholeBookAll || reviewSectionIndex <= 0}
            onClick={() => navigateWholeBookReview(reviewSections[reviewSectionIndex - 1].id)}
          >
            {isFR ? "← Précédent" : "← Previous"}
          </button>
          <button
            className="ns-button ns-button-outline"
            type="button"
            data-novel-story-whole-book-next={position}
            disabled={showWholeBookAll || reviewSectionIndex < 0 || reviewSectionIndex >= reviewSections.length - 1}
            onClick={() => navigateWholeBookReview(reviewSections[reviewSectionIndex + 1].id)}
          >
            {isFR ? "Suivant →" : "Next →"}
          </button>
          <button
            className="ns-button ns-button-outline"
            type="button"
            data-novel-story-whole-book-read-all={position}
            onClick={() =>
              navigateWholeBookReview(showWholeBookAll ? firstReviewSection : "all")
            }
          >
            {showWholeBookAll
              ? (isFR ? "Lire par section" : "Read by Section")
              : (isFR ? "Tout lire" : "Read All")}
          </button>
          <span className="ns-card-meta" aria-live="polite">
            {showWholeBookAll
              ? (isFR ? "Toutes les sections" : "All sections")
              : `${reviewSectionIndex + 1} / ${reviewSections.length}`}
          </span>
        </nav>
      );
    }

    const sectionTitleStyle = {
      fontSize: 13,
      fontWeight: 900,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#047857",
      margin: "0 0 14px",
    };
    const sectionCardStyle = {
      padding: "clamp(16px, 3vw, 32px)",
      background: "#ffffff",
      border: "1px solid rgba(148,163,184,0.3)",
      borderRadius: 16,
      lineHeight: 1.8,
      overflowWrap: "anywhere",
    };

    function renderBookMatterItems(kind, items) {
      return items.map((item, index) => (
        <article
          key={item.id}
          data-novel-story-whole-book-matter-item={kind}
          style={{
            paddingBottom: 24,
            marginBottom: index < items.length - 1 ? 24 : 0,
            borderBottom: index < items.length - 1
              ? "1px solid rgba(148,163,184,0.25)" : "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0 }}>{clean(item.title)}</h3>
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() => {
                setMatterEditorKind(kind);
                setMatterEditorItemId(item.id);
              }}
            >
              {isFR ? "Modifier" : "Edit"}
            </button>
          </div>
          <ChildrensBookManuscriptReadOnly
            content={item.manuscriptContent || null}
            fallbackText={String(item.text || "")}
            lang={isFR ? "fr" : "en"}
          />
        </article>
      ));
    }

    return (
      <div
        data-novel-story-whole-book-review="true"
        style={{ display: "grid", gap: 16 }}
      >
        <div
          className="ns-card"
          style={{
            padding: 20,
            borderRadius: 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div className="ns-card-meta" style={{ fontWeight: 900, letterSpacing: "0.09em" }}>
              {isFR ? "LECTURE DU LIVRE ENTIER" : "WHOLE-BOOK REVIEW"}
            </div>
            <h2 style={{ margin: "5px 0" }}>{storyTitle(story, project, isFR)}</h2>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {isFR
                ? "Pages liminaires → chapitres → éléments de fin. Les scènes non affectées sont séparées."
                : "Front Matter → Chapters → Back Matter. Unassigned Scenes are separate."}
            </p>
            <p className="ns-card-meta" style={{ margin: "5px 0 0" }}>
              {frontItems.length} {isFR ? "éléments préliminaires" : "Front Matter items"} ·{" "}
              {chapters.length} {isFR ? "chapitres" : "Chapters"} ·{" "}
              {assignedCount} {isFR ? "scènes affectées" : "assigned Scenes"} ·{" "}
              {backItems.length} {isFR ? "éléments finaux" : "Back Matter items"}
            </p>
          </div>
          <button
            className="ns-button ns-button-outline"
            data-novel-story-close-whole-book-review="true"
            type="button"
            onClick={() => setWholeBookReviewOpen(false)}
          >
            {manuscriptWorkspace === "review"
              ? (isFR ? "← Retour à la révision" : "← Back to Review")
              : (isFR ? "← Retour à la structure du livre" : "← Back to Book Structure")}
          </button>
        </div>

        {renderWholeBookNavigator("top")}

        {(showWholeBookAll || activeReviewSection === "front") && frontItems.length > 0 ? (
          <section data-novel-story-whole-book-front="true" style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>{isFR ? "PAGES LIMINAIRES" : "FRONT MATTER"}</h3>
            {renderBookMatterItems("front", frontItems)}
          </section>
        ) : null}

        <div data-novel-story-whole-book-chapters="true" style={{ display: "grid", gap: 16 }}>
          {chapters.map((chapter, chapterIndex) => (
            showWholeBookAll || activeReviewSection === `chapter:${chapter.id}` ? (
            <section
              key={chapter.id}
              data-novel-story-whole-book-chapter="true"
              style={sectionCardStyle}
            >
              <div style={sectionTitleStyle}>
                {isFR ? "CHAPITRE" : "CHAPTER"} {chapterIndex + 1}
              </div>
              <h2 style={{ margin: "0 0 20px" }}>
                {clean(chapter.title) ||
                  (isFR ? "Chapitre " : "Chapter ") + (chapterIndex + 1)}
              </h2>
              {safeArray(chapter.sceneIds).length > 0 ? (
                safeArray(chapter.sceneIds).map((sceneId, sceneIndex) => {
                  const scene = sceneById.get(sceneId);
                  if (!scene) return null;
                  return (
                    <article
                      key={sceneId}
                      data-novel-story-whole-book-scene="true"
                      style={{
                        paddingBottom: 20,
                        marginBottom: sceneIndex < chapter.sceneIds.length - 1 ? 22 : 0,
                        borderBottom: sceneIndex < chapter.sceneIds.length - 1
                          ? "1px solid rgba(148,163,184,0.25)" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 10,
                        }}
                      >
                        <div>
                          <span className="ns-card-meta">
                            {isFR ? "Scène" : "Scene"} {sceneIndex + 1} / {chapter.sceneIds.length}
                          </span>
                          <h3 style={{ margin: "4px 0" }}>
                            {sceneTitle(scene, sceneIndex + 1, isFR)}
                          </h3>
                        </div>
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() => openSceneForEditing(sceneId)}
                        >
                          {isFR ? "Modifier" : "Edit"}
                        </button>
                      </div>
                      <NovelStorySceneReadOnly scene={scene} lang={isFR ? "fr" : "en"} />
                    </article>
                  );
                })
              ) : (
                <p className="ns-card-meta">
                  {isFR ? "Aucune scène affectée pour le moment." : "No assigned Scenes yet."}
                </p>
              )}
            </section>
            ) : null
          ))}
        </div>

        {(showWholeBookAll || activeReviewSection === "back") && backItems.length > 0 ? (
          <section data-novel-story-whole-book-back="true" style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>{isFR ? "ÉLÉMENTS DE FIN" : "BACK MATTER"}</h3>
            {renderBookMatterItems("back", backItems)}
          </section>
        ) : null}

        {(showWholeBookAll || activeReviewSection === "unassigned") && unassignedSceneIds.length > 0 ? (
          <details
            data-novel-story-whole-book-unassigned="true"
            style={{
              padding: 20,
              border: "1px dashed rgba(13,148,136,0.55)",
              borderLeft: "6px solid #0d9488",
              background: "#f0fdfa",
              borderRadius: 14,
            }}
          >
            <summary style={{ fontWeight: 850, cursor: "pointer" }}>
              {isFR ? "Scènes non affectées · hors du manuscrit" :
                "Unassigned Scenes · NOT PART OF THE MANUSCRIPT"} ({unassignedSceneIds.length})
            </summary>
            <p className="ns-card-meta">
              {isFR
                ? (isChildrensBridge
                    ? "Ces scènes ne sont pas affectées à un chapitre enregistré."
                    : "Ces scènes Canon ne sont pas affectées à un chapitre et ne figurent pas dans le livre assemblé.")
                : (isChildrensBridge
                    ? "These Scenes have no saved Chapter assignment."
                    : "These approved Canon Scenes have no Chapter assignment and are not included in the assembled book.")}
            </p>
            {unassignedSceneIds.map((sceneId, index) => {
              const scene = sceneById.get(sceneId);
              if (!scene) return null;
              return (
                <article key={sceneId} style={{ padding: "14px 0" }}>
                  <h4 style={{ margin: "0 0 8px" }}>
                    {sceneTitle(scene, index + 1, isFR)}
                  </h4>
                  <NovelStorySceneReadOnly scene={scene} lang={isFR ? "fr" : "en"} />
                </article>
              );
            })}
          </details>
        ) : null}

        {reviewSections.length === 0 ? (
          <p className="ns-card-meta" data-novel-story-whole-book-empty="true">
            {isFR
              ? "Le livre ne contient pas encore de contenu. Ajoutez des préliminaires, des chapitres ou des éléments de fin."
              : "This book has no content yet. Add Front Matter, Chapters, or Back Matter to begin."}
          </p>
        ) : null}

        {!showWholeBookAll && reviewSections.length > 1 ? renderWholeBookNavigator("bottom") : null}
      </div>
    );
  }

  function renderBookStructure() {
    // Keep the exact established Matter catalogs; only their visibility
    // is simplified for native Children's Books.
    const matterCatalogsV1F2F = (
      <div data-novel-story-matter-structure-templates="true"
        style={{ display: "grid", gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))" }}>
        {renderMatterTemplateCatalog("front")}
        {renderMatterTemplateCatalog("back")}
      </div>
    );
    return (
      <div
        data-novel-story-book-structure="true"
        style={{ display: "grid", gap: 16 }}
      >
        <section
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
            border:
              "1px solid rgba(16,185,129,0.30)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#047857",
            }}
          >
            {isFR
              ? "STRUCTURE PRINCIPALE DU LIVRE"
              : "MAIN BOOK STRUCTURE"}
          </div>

          <h3
            className="ns-section-title"
            style={{ margin: "5px 0 6px" }}
          >
            {isFR
              ? "Planifier les chapitres"
              : "Plan Your Chapters"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.55,
              maxWidth: 940,
            }}
          >
            {isFR
              ? (isChildrensBridge
                  ? "Consultez les chapitres et scènes de ce livre jeunesse. Les modifications de structure nécessitent une sauvegarde vérifiée."
                  : story?.bookCategory === "children_book"
                  ? "Construisez ce livre jeunesse chapitre par chapitre. Vous pouvez gérer les chapitres et leurs scènes ici."
                  : "Construisez le roman chapitre par chapitre. Cliquez sur un chapitre pour le renommer, le déplacer, le dupliquer, organiser ses scènes ou commencer à écrire.")
              : (isChildrensBridge
                  ? "Review this Children's Book by Chapter and Scene. Structure changes require verified saving."
                  : story?.bookCategory === "children_book"
                  ? "Build your Children's Book one Chapter at a time. Rename, arrange, review, or continue writing here."
                  : "Build the novel one Chapter at a time. Use each Chapter to rename, move, duplicate, arrange its Scenes, or start writing.")}
          </p>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              data-novel-story-open-whole-book-review="true"
              onClick={() => setWholeBookReviewOpen(true)}
            >
              {isFR ? "Lire le livre entier" : "Read Whole Book Draft"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={addChapter}
              data-novel-story-add-chapter="true"
            >
              {chapters.length === 0
                ? (isFR ? "+ Ajouter un chapitre — recommandé" : "+ Add Chapter — Recommended")
                : (isFR ? "+ Ajouter un chapitre" : "+ Add Chapter")}
            </button>

            {isStructurePreview && !isChildrensBridge ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={resetDraft}
              >
                {isFR
                  ? "Réinitialiser l’aperçu"
                  : "Reset Preview"}
              </button>
            ) : (
              <span
                className="ns-card-meta"
                role="status"
                style={{
                  alignSelf: "center",
                  fontWeight: 800,
                }}
              >
                {savingStructure
                  ? (isFR ? "Enregistrement…" : "Saving…")
                  : structureMessage}
              </span>
            )}
          </div>

          {chapterNavigator()}
        </section>

        {story?.bookCategory === "children_book" && !isChildrensBridge ? (
          <details data-native-childrens-optional-matter-v1f2f="structure"
            style={{ padding: "14px 18px", border: "1px solid #d1e5dd",
              borderRadius: 14, background: "#ffffff" }}>
            <summary style={{ cursor: "pointer", fontWeight: 800, color: "#065f46" }}>
              {isFR ? "Sections facultatives · Préliminaires et Postliminaires"
                : "Optional book sections · Front Matter and Back Matter"}
            </summary>
            <div style={{ marginTop: 14 }}>{matterCatalogsV1F2F}</div>
          </details>
        ) : matterCatalogsV1F2F}

        <section
          className="ns-card"
          data-novel-story-unassigned-book-structure="true"
          style={{
            padding: 20,
            borderRadius: 18,
            border: "1px solid rgba(13,148,136,0.34)",
            borderLeft: "7px solid #0d9488",
            background: "linear-gradient(120deg, rgba(240,253,250,0.95), #ffffff 53%)",
            boxShadow: "0 6px 18px rgba(15,118,110,0.07)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h3
              className="ns-section-title"
              style={{
                margin: 0,
                fontSize: 15,
                letterSpacing: "0.11em",
                fontWeight: 900,
                color: "#0f766e",
                textTransform: "uppercase",
              }}
            >
              {isFR ? "SCÈNES NON AFFECTÉES" : "UNASSIGNED SCENES"}
            </h3>
            <strong
              className="ns-card-meta"
              data-novel-story-unassigned-count="true"
              aria-live="polite"
            >
              {unassignedSceneIds.length}{" "}
              {isFR
                ? (unassignedSceneIds.length === 1 ? "scène" : "scènes")
                : (unassignedSceneIds.length === 1 ? "Scene" : "Scenes")}
            </strong>
          </div>

          <p className="ns-card-meta" style={{ margin: "8px 0 12px" }}>
            {isFR
              ? (isChildrensBridge
                  ? "Les pages et doubles pages d’origine restent accessibles dans la section ci-dessous."
                  : "Les scènes Canon sans chapitre restent ici jusqu’à ce que vous choisissiez leur chapitre dans « Déplacer vers un chapitre… ».")
              : (isChildrensBridge
                  ? "Original pages and spreads remain available in the section below."
                  : "Canon Scenes without a Chapter stay here until you assign them using “Move to Chapter…”.")}
          </p>

          {unassignedSceneIds.length > 0 ? (
            <>
              <div
                data-novel-story-unassigned-search-controls="true"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <label style={{ flex: "1 1 260px" }}>
                  <span className="ns-card-meta">
                    {isFR ? "Trouver une scène" : "Find a Scene"}
                  </span>
                  <input
                    className="ns-input"
                    type="search"
                    aria-label={isFR ? "Rechercher une scène non affectée" : "Search Unassigned Scenes"}
                    data-novel-story-unassigned-search="true"
                    placeholder={isFR ? "Rechercher par titre ou texte…" : "Search titles or text…"}
                    value={unassignedSceneSearch}
                    onChange={(event) => {
                      setUnassignedSceneSearch(event.target.value);
                      setUnassignedScenePage(0);
                      setPreviewUnassignedSceneId("");
                    }}
                    style={{ width: "100%", maxWidth: "none" }}
                  />
                </label>
                <span className="ns-card-meta" aria-live="polite">
                  {filteredUnassignedSceneIds.length}
                  {" / "}
                  {unassignedSceneIds.length}
                  {isFR ? " scènes" : " Scenes"}
                </span>
              </div>

              {pagedUnassignedSceneIds.length > 0 ? (
                renderUnassignedSceneRows(pagedUnassignedSceneIds, true)
              ) : (
                <p className="ns-card-meta" role="status">
                  {isFR ? "Aucune scène ne correspond à la recherche." : "No Scenes match this search."}
                </p>
              )}

              {unassignedScenePageCount > 1 ? (
                <div
                  data-novel-story-unassigned-pagination="true"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: 14,
                  }}
                >
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    disabled={visibleUnassignedScenePage === 0}
                    onClick={() => {
                      setUnassignedScenePage(Math.max(0, visibleUnassignedScenePage - 1));
                      setPreviewUnassignedSceneId("");
                    }}
                  >
                    {isFR ? "Précédent" : "Previous"}
                  </button>
                  <span className="ns-card-meta" aria-live="polite">
                    {isFR ? "Page" : "Page"} {visibleUnassignedScenePage + 1} / {unassignedScenePageCount}
                  </span>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    disabled={visibleUnassignedScenePage >= unassignedScenePageCount - 1}
                    onClick={() => {
                      setUnassignedScenePage(Math.min(unassignedScenePageCount - 1, visibleUnassignedScenePage + 1));
                      setPreviewUnassignedSceneId("");
                    }}
                  >
                    {isFR ? "Suivant" : "Next"}
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p
              className="ns-card-meta"
              data-novel-story-unassigned-empty="true"
              style={{ margin: 0 }}
            >
              {isFR
                ? (isChildrensBridge
                    ? "Aucune scène non affectée dans cette vue. Les pages d’origine restent disponibles ci-dessous."
                    : "Aucune scène non affectée. Les nouvelles scènes Canon sans chapitre apparaîtront ici.")
                : (isChildrensBridge
                    ? "No unassigned Scenes in this view. Original pages and spreads remain available below."
                    : "No unassigned Scenes. New Canon Scenes without a Chapter will appear here.")}
            </p>
          )}
        </section>

        <div
          data-novel-story-structure-chapter-list="true"
          style={{
            display: "grid",
            gap: 14,
          }}
        >
          {chapters.map(
            (chapter, chapterIndex) => {
              const selected =
                chapterNavigatorId === chapter.id;
              const editing =
                editingChapterId === chapter.id;
              const moving =
                moveChapterPositionId === chapter.id;

              return (
                <article
                  key={chapter.id}
                  className={
                    "ns-card ns-novel-story-structure-chapter-card" +
                    (selected ? " is-selected" : "")
                  }
                  id={"novel-story-structure-" + chapter.id}
                  data-novel-story-chapter-card="true"
                  data-selected={selected ? "true" : "false"}
                  aria-current={selected ? "true" : undefined}
                  tabIndex={0}
                  onClick={() =>
                    setChapterNavigatorId(chapter.id)
                  }
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();
                      setChapterNavigatorId(chapter.id);
                    }
                  }}
                  style={{
                    padding: 18,
                    borderRadius: 18,
                    border: selected
                      ? "2px solid #2563eb"
                      : "1px solid rgba(148,163,184,0.38)",
                    borderLeft:
                      "7px solid " +
                      (selected
                        ? "#2563eb"
                        : "#059669"),
                    background: selected
                      ? "linear-gradient(135deg, #eff6ff 0%, #ffffff 62%)"
                      : "#ffffff",
                    boxShadow: selected
                      ? "0 12px 28px rgba(37,99,235,0.12)"
                      : undefined,
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      gap: 14,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        minWidth: 220,
                        flex: "1 1 300px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "#047857",
                        }}
                      >
                        {(isFR
                          ? "CHAPITRE "
                          : "CHAPTER ") +
                          (chapterIndex + 1) +
                          " " +
                          (isFR ? "SUR " : "OF ") +
                          chapters.length}
                      </div>

                      {editing ? (
                        <input
                          className="ns-input"
                          value={chapter.title}
                          onChange={(event) =>
                            renameChapter(
                              chapter.id,
                              event.target.value
                            )
                          }
                          style={{
                            marginTop: 5,
                            maxWidth: 520,
                            fontWeight: 850,
                          }}
                        />
                      ) : (
                        <h4
                          style={{
                            margin: "5px 0 0",
                            fontSize: 19,
                          }}
                        >
                          {clean(chapter.title) ||
                            (isFR
                              ? "Chapitre sans titre"
                              : "Untitled Chapter")}
                        </h4>
                      )}

                      <div
                        className="ns-card-meta"
                        style={{ marginTop: 6 }}
                      >
                        {chapter.sceneIds.length}{" "}
                        {chapter.sceneIds.length === 1
                          ? (isFR
                              ? "scène"
                              : "scene")
                          : (isFR
                              ? "scènes"
                              : "scenes")}
                      </div>
                    </div>

                    <div className="ns-novel-story-chapter-primary-actions">
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        data-novel-story-review-chapter-action="true"
                        onClick={() => openChapter(chapter.id)}
                      >
                        {isFR ? "Lire et réviser le chapitre" : "Read / Review Chapter"}
                      </button>
                      {story?.bookCategory === "children_book" ? (
                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          data-native-childrens-manage-scenes-v1f2d="true"
                          onClick={() => openChapterSceneCards(chapter.id)}
                        >
                          {isFR ? "Gérer les scènes" : "Manage Scene Cards"}
                        </button>
                      ) : null}
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        onClick={() =>
                          continueChapterWriting(
                            chapter
                          )
                        }
                      >
                        {chapter.sceneIds.length > 0
                          ? (
                              isFR
                                ? "Continuer à écrire"
                                : "Continue Writing"
                            )
                          : (
                              isFR
                                ? "Écrire la première scène"
                                : "Write First Scene"
                            )}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          finishChapterEditing(
                            chapter.id
                          )
                        }
                      >
                        {editing
                          ? (isFR
                              ? "Terminer"
                              : "Done Editing")
                          : (isFR
                              ? "Modifier"
                              : "Edit")}
                      </button>

                      <details className="ns-novel-story-chapter-tools">
                        <summary>
                          {isFR
                            ? "Outils du chapitre"
                            : "Chapter Tools"}
                        </summary>

                        <div className="ns-novel-story-chapter-tools-grid">
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={chapterIndex === 0}
                            onClick={() =>
                              moveChapter(
                                chapter.id,
                                "up"
                              )
                            }
                          >
                            {isFR
                              ? "Plus tôt"
                              : "Move Earlier"}
                          </button>

                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={
                              chapterIndex ===
                              chapters.length - 1
                            }
                            onClick={() =>
                              moveChapter(
                                chapter.id,
                                "down"
                              )
                            }
                          >
                            {isFR
                              ? "Plus tard"
                              : "Move Later"}
                          </button>

                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            onClick={() =>
                              setMoveChapterPositionId(
                                moving
                                  ? ""
                                  : chapter.id
                              )
                            }
                          >
                            {isFR
                              ? "Déplacer à la position"
                              : "Move to Position"}
                          </button>

                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={isChildrensBridge}
                            title={isChildrensBridge ? 'Children’s Book chapter duplication is not yet available' : undefined}
                            onClick={() =>
                              duplicateChapter(
                                chapter.id
                              )
                            }
                          >
                            {isFR
                              ? "Dupliquer"
                              : "Duplicate"}
                          </button>

                          <button
                            className="ns-button ns-button-outline ns-novel-story-danger-button"
                            type="button"
                            disabled={isChildrensBridge}
                            title={isChildrensBridge ? 'Children’s Book chapter removal is not yet available' : undefined}
                            onClick={() =>
                              requestRemoveChapter(
                                chapter.id
                              )
                            }
                          >
                            {isFR
                              ? "Retirer"
                              : "Remove"}
                          </button>
                        </div>

                        {moving ? (
                          <div className="ns-novel-story-chapter-position-row">
                            <strong>
                              {isFR
                                ? "Nouvelle position"
                                : "New position"}
                            </strong>

                            <select
                              className="ns-input"
                              defaultValue={
                                String(
                                  chapterIndex + 1
                                )
                              }
                              onChange={(event) =>
                                moveChapterToPosition(
                                  chapter.id,
                                  event.target.value
                                )
                              }
                              style={{
                                width: "auto",
                                minWidth: 120,
                              }}
                            >
                              {chapters.map(
                                (_, index) => (
                                  <option
                                    key={index + 1}
                                    value={index + 1}
                                  >
                                    {index + 1}
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        ) : null}
                      </details>
                    </div>
                  </div>
                </article>
              );
            }
          )}
        </div>
      </div>
    );
  }

  function renderWriteChapters() {
    const rangeEnd = Math.min(
      chapters.length,
      chapterRangeStart + CHAPTER_RANGE_SIZE
    );

    return (
      <div
        data-novel-story-write-chapters="true"
        style={{ display: "grid", gap: 16 }}
      >
        <section
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent:
                "space-between",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#047857",
                }}
              >
                {isFR ? "ÉCRIRE" : "WRITE"}
              </div>
              <h3
                className="ns-section-title"
                style={{
                  margin: "5px 0 6px",
                }}
              >
                {storyTitle(
                  story,
                  project,
                  isFR
                )}
              </h3>
              <p
                className="ns-card-meta"
                style={{ margin: 0 }}
              >
                {isFR
                  ? (isChildrensBridge
                      ? "Choisissez un chapitre pour écrire ou continuer. La structure du livre conserve l’ordre des chapitres."
                      : story?.bookCategory === "children_book"
                      ? "Choisissez un chapitre pour écrire ou continuer. La structure du livre conserve l’ordre des chapitres."
                      : "Choisissez un chapitre pour commencer ou continuer l’écriture. Structure du livre contrôle l’ordre du roman.")
                  : (isChildrensBridge
                      ? "Choose a Chapter to start or continue writing. Book Structure controls the Chapter order."
                      : story?.bookCategory === "children_book"
                      ? "Choose a Chapter to write or continue. Book Structure controls your Children's Book chapter order."
                      : "Choose a Chapter to start or continue writing. Book Structure controls the order of the novel.")}
              </p>
            </div>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                setManuscriptWorkspace(
                  "book-structure"
                )
              }
            >
              {isFR
                ? "Gérer les chapitres"
                : "Manage Chapters"}
            </button>
          </div>

          <div
            style={{
              marginTop: 18,
              padding: 16,
              borderRadius: 16,
              background:
                "rgba(248,250,252,0.92)",
              border:
                "1px solid rgba(148,163,184,0.28)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <strong>
                {isFR
                  ? (chapters.length === 1 ? "Chapitre " : "Chapitres ")
                  : (chapters.length === 1 ? "Chapter " : "Chapters ")}
                {chapters.length
                  ? chapterRangeStart + 1
                  : 0}
                {"–"}
                {rangeEnd}
                {" "}
                {isFR ? "sur " : "of "}
                {chapters.length}
              </strong>

              <span className="ns-card-meta">
                {chapters.length}{" "}
                {isFR
                  ? (chapters.length === 1 ? "chapitre" : "chapitres")
                  : (chapters.length === 1 ? "chapter" : "chapters")}{" "}
                · {sceneRecords.length}{" "}
                {isFR
                  ? (sceneRecords.length === 1 ? "scène" : "scènes")
                  : (sceneRecords.length === 1 ? "scene" : "scenes")}
              </span>
            </div>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 800,
                marginTop: 14,
              }}
            >
              <span>
                {isFR
                  ? "Aller au chapitre"
                  : "Jump to Chapter"}
              </span>
              <select
                className="ns-input"
                defaultValue=""
                onChange={(event) => {
                  const id =
                    event.target.value;
                  if (!id) return;
                  continueChapterWriting(
                    chapters.find((chapter) => chapter.id === id)
                  );
                }}
              >
                <option value="">
                  {isFR
                    ? "Choisir un chapitre…"
                    : "Choose a Chapter…"}
                </option>
                {chapters.map(
                  (
                    chapter,
                    chapterIndex
                  ) => (
                    <option
                      key={chapter.id}
                      value={chapter.id}
                    >
                      {clean(chapter.title) ||
                      ((isFR
                        ? "Chapitre "
                        : "Chapter ") +
                        (chapterIndex + 1))}
                    </option>
                  )
                )}
              </select>
            </label>
          </div>

          <div
            data-novel-story-write-chapter-grid="true"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 16,
              marginTop: 18,
            }}
          >
            {visibleChapters.map(
              (
                chapter,
                visibleIndex
              ) => {
                const chapterIndex =
                  chapterRangeStart +
                  visibleIndex;
                const words =
                  chapterWords(chapter);
                const started =
                  chapter.sceneIds.length > 0;

                return (
                  <article
                    key={chapter.id}
                    className="ns-card"
                    data-novel-story-write-chapter-card="true"
                    style={{
                      padding: 18,
                      borderRadius: 18,
                      minHeight: 165,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        gap: 10,
                        alignItems:
                          "flex-start",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: 20,
                        }}
                      >
                        {clean(chapter.title) ||
                          (isFR
                            ? "Chapitre " +
                              (chapterIndex + 1)
                            : "Chapter " +
                              (chapterIndex + 1))}
                      </h4>

                      <span
                        style={{
                          borderRadius: 999,
                          padding: "6px 10px",
                          fontSize: 12,
                          fontWeight: 900,
                          background: started
                            ? "#dcfce7"
                            : "#fef3c7",
                          color: started
                            ? "#166534"
                            : "#92400e",
                        }}
                      >
                        {chapterStatus(
                          chapter
                        )}
                      </span>
                    </div>

                    <div
                      className="ns-card-meta"
                      style={{
                        marginTop: 12,
                        fontWeight: 750,
                      }}
                    >
                      {chapter.sceneIds.length}{" "}
                      {chapter.sceneIds.length === 1
                        ? (isFR
                            ? "scène"
                            : "scene")
                        : (isFR
                            ? "scènes"
                            : "scenes")}
                      {words > 0
                        ? " · " +
                          words.toLocaleString() +
                          " " +
                          (isFR
                            ? "mot(s)"
                            : "word(s)")
                        : ""}
                    </div>

                    <button
                      className={
                        started
                          ? "ns-button ns-button-primary"
                          : "ns-button ns-button-outline"
                      }
                      type="button"
                      onClick={() =>
                        continueChapterWriting(
                          chapter
                        )
                      }
                      style={{
                        marginTop: 18,
                      }}
                    >
                      {started
                        ? (isFR
                            ? "Continuer l’écriture"
                            : "Continue Writing")
                        : (isFR
                            ? "Commencer à écrire"
                            : "Start Writing")}
                    </button>
                  </article>
                );
              }
            )}
          </div>

          {chapters.length >
          CHAPTER_RANGE_SIZE ? (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 8,
                justifyContent:
                  "space-between",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  chapterRangeStart === 0
                }
                onClick={() =>
                  setChapterRangeStart(
                    Math.max(
                      0,
                      chapterRangeStart -
                        CHAPTER_RANGE_SIZE
                    )
                  )
                }
              >
                {isFR
                  ? "Plage précédente"
                  : "Previous Range"}
              </button>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  rangeEnd >=
                  chapters.length
                }
                onClick={() =>
                  setChapterRangeStart(
                    chapterRangeStart +
                      CHAPTER_RANGE_SIZE
                  )
                }
              >
                {isFR
                  ? "Plage suivante"
                  : "Next Range"}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    );
  }

  function renderChapterReview() {
    if (!activeChapter) return null;
    return (
      <section
        className="ns-card"
        data-novel-story-chapter-review="true"
        style={{
          padding: 22,
          borderRadius: 18,
          border: "1px solid rgba(5,150,105,0.24)",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <div
            className="ns-card-meta"
            style={{ fontWeight: 900, letterSpacing: "0.08em" }}
          >
            {isFR ? "MANUSCRIT DU CHAPITRE" : "CHAPTER MANUSCRIPT"}
          </div>
          <h3 style={{ margin: "5px 0" }}>{clean(activeChapter.title)}</h3>
          <p className="ns-card-meta" style={{ margin: "0 0 5px" }}>
            {activeChapter.sceneIds.length} {isChildrensBridge
              ? (isFR ? (activeChapter.sceneIds.length === 1 ? "scène" : "scènes")
                      : (activeChapter.sceneIds.length === 1 ? "Scene" : "Scenes"))
              : (isFR ? "scènes" : "Scenes")} ·{" "}
            {chapterWords(activeChapter).toLocaleString()} {isFR ? "mots" : "words"}
          </p>
          <p className="ns-card-meta" style={{ margin: 0 }}>
            {isFR
              ? "Lisez les scènes dans l’ordre enregistré. Déplacez une scène, ou ouvrez Modifier pour réviser son texte sans changer son identité."
              : "Read the Scenes in their saved order. Move any Scene, or select Edit to refine the exact saved text."}
          </p>
          {savingStructure ? (
            <p role="status" className="ns-card-meta">
              {isFR ? "Enregistrement de l’ordre…" : "Saving order…"}
            </p>
          ) : structureMessage ? (
            <p role="status" className="ns-card-meta">{structureMessage}</p>
          ) : null}
        </div>

        {activeChapter.sceneIds.length === 0 ? (
          <p className="ns-card-meta" data-novel-story-chapter-review-empty="true">
            {isFR ? "Ce chapitre n’a pas encore de scènes. Ajoutez-en une pour commencer." : "This Chapter has no Scenes yet. Add one to begin."}
          </p>
        ) : (
          <div
            data-novel-story-chapter-reading-surface="true"
            style={{
              maxHeight: "min(75vh, 880px)",
              overflowY: "auto",
              padding: "clamp(12px, 2.5vw, 28px)",
              border: "1px solid rgba(148,163,184,0.28)",
              borderRadius: 14,
              background: "#fff",
            }}
          >
            {activeChapter.sceneIds.map((sceneId, sceneIndex) => {
              const scene = sceneById.get(sceneId);
              if (!scene) return null;
              return (
                <article
                  key={sceneId}
                  data-novel-story-chapter-review-scene="true"
                  style={{
                    paddingBottom: 26,
                    marginBottom: 24,
                    borderBottom: sceneIndex < activeChapter.sceneIds.length - 1
                      ? "1px solid rgba(148,163,184,0.26)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 10,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span className="ns-card-meta">
                        {isFR ? "Scène" : "Scene"} {sceneIndex + 1} / {activeChapter.sceneIds.length}
                      </span>
                      <h4 style={{ margin: "4px 0" }}>
                        {sceneTitle(scene, sceneIndex + 1, isFR)}
                      </h4>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() => openSceneForEditing(sceneId)}
                        data-novel-story-chapter-review-edit="true"
                      >
                        {isFR ? "Modifier" : "Edit"}
                      </button>
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={savingStructure || sceneIndex === 0}
                        onClick={() => moveSceneWithin(activeChapter.id, sceneId, "up")}
                      >
                        {isFR ? "Plus tôt" : "Earlier"}
                      </button>
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={savingStructure || sceneIndex === activeChapter.sceneIds.length - 1}
                        onClick={() => moveSceneWithin(activeChapter.id, sceneId, "down")}
                      >
                        {isFR ? "Plus tard" : "Later"}
                      </button>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="ns-card-meta">
                          {isFR ? "Position" : "Position"}
                        </span>
                        <select
                          className="ns-input"
                          data-novel-story-chapter-review-position="true"
                          aria-label={isFR ? "Position de la scène" : "Scene position"}
                          value={String(sceneIndex + 1)}
                          disabled={savingStructure || activeChapter.sceneIds.length < 2}
                          onChange={(event) =>
                            moveSceneToPosition(activeChapter.id, sceneId, event.target.value)
                          }
                          style={{ width: "auto", minWidth: 68 }}
                        >
                          {activeChapter.sceneIds.map((_, index) => (
                            <option key={index + 1} value={String(index + 1)}>
                              {index + 1}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div
                    data-novel-story-chapter-review-text="true"
                    style={{
                      fontSize: "1.04rem",
                      lineHeight: 1.85,
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <NovelStorySceneReadOnly scene={scene} lang={isFR ? "fr" : "en"} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  function renderSceneStructure() {
    if (!activeChapter) {
      return (
        <section
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
          }}
        >
          {isFR
            ? "Choisissez un chapitre."
            : "Choose a Chapter."}
        </section>
      );
    }

    const chapterIndex =
      chapters.findIndex(
        (chapter) =>
          chapter.id === activeChapter.id
      );

    return (
      <div
        data-novel-story-scene-structure="true"
        style={{ display: "grid", gap: 16 }}
      >
        <section
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
            border:
              "1px solid rgba(16,185,129,0.30)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent:
                "space-between",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#047857",
                }}
              >
                {(isFR
                  ? "CHAPITRE "
                  : "CHAPTER ") +
                  (chapterIndex + 1) +
                  " " +
                  (isFR ? "SUR " : "OF ") +
                  chapters.length}
              </div>

              <h3
                className="ns-section-title"
                style={{
                  margin: "5px 0 6px",
                }}
              >
                {clean(activeChapter.title)}
              </h3>

              <p
                className="ns-card-meta"
                style={{ margin: 0 }}
              >
                {isFR
                  ? "Les scènes utilisent le même ordre et les mêmes contrôles éprouvés que Structure du livre."
                  : "Scenes use the same proven ordering and management pattern as Book Structure."}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  setManuscriptWorkspace(
                    "write"
                  )
                }
              >
                ← {isFR
                  ? "Tous les chapitres"
                  : "All Chapters"}
              </button>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  setManuscriptWorkspace(
                    "book-structure"
                  )
                }
              >
                {isFR
                  ? "Gérer les chapitres"
                  : "Manage Chapters"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 16,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                addScene(activeChapter.id)
              }
              data-novel-story-add-scene="true"
            >
              {isFR
                ? "+ Ajouter une scène"
                : "+ Add Scene"}
            </button>

            <span
              className="ns-card-meta"
              style={{
                alignSelf: "center",
              }}
            >
              {activeChapter.sceneIds.length}{" "}
              {isChildrensBridge
                ? (isFR ? (activeChapter.sceneIds.length === 1 ? "scène" : "scènes")
                        : (activeChapter.sceneIds.length === 1 ? "scene" : "scenes"))
                : (isFR ? "scènes" : "scenes")} ·{" "}
              {chapterWords(
                activeChapter
              ).toLocaleString()}{" "}
              {isFR ? "mots" : "words"}
            </span>
          </div>
        </section>

        <div
          data-novel-story-chapter-view-switch="true"
          style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
        >
          <button
            className={chapterReviewOpen ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
            type="button"
            onClick={() => setChapterReviewOpen(true)}
          >
            {isFR ? "Lire le chapitre" : "Read / Review Chapter"}
          </button>
          <button
            className={!chapterReviewOpen ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
            type="button"
            onClick={() => setChapterReviewOpen(false)}
          >
            {isFR ? "Gérer les scènes" : "Manage Scene Cards"}
          </button>
        </div>

        {chapterReviewOpen ? renderChapterReview() : (
        <div
          data-novel-story-scene-list="true"
          style={{
            display: "grid",
            gap: 14,
          }}
        >
          {activeChapter.sceneIds.map(
            (sceneId, sceneIndex) => {
              const scene =
                sceneById.get(sceneId);

              if (!scene) return null;

              const moving =
                moveScenePositionId ===
                sceneId;

              return (
                <article
                  className="ns-card"
                  data-novel-story-scene-card="true"
                  key={sceneId}
                  style={{
                    padding: 18,
                    borderRadius: 18,
                    border:
                      "1px solid rgba(148,163,184,0.38)",
                    borderLeft:
                      "7px solid #059669",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      gap: 14,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        minWidth: 220,
                        flex: "1 1 300px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing:
                            "0.08em",
                          textTransform:
                            "uppercase",
                          color: "#047857",
                        }}
                      >
                        {(isFR
                          ? "SCÈNE "
                          : "SCENE ") +
                          (sceneIndex + 1) +
                          " " +
                          (isFR
                            ? "SUR "
                            : "OF ") +
                          activeChapter
                            .sceneIds.length}
                      </div>

                      <h4
                        style={{
                          margin: "5px 0 0",
                          fontSize: 19,
                        }}
                      >
                        {sceneTitle(
                          scene,
                          sceneIndex + 1,
                          isFR
                        )}
                      </h4>

                      <div
                        className="ns-card-meta"
                        style={{ marginTop: 6 }}
                      >
                        {wordCount(
                          sceneText(scene)
                        ).toLocaleString()}{" "}
                        {isFR
                          ? "mots"
                          : "words"}{" "}
                        · {isChildrensBridge ? (isFR ? "Scène" : "Scene") : "Canon"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 7,
                        flexWrap: "wrap",
                        justifyContent:
                          "flex-end",
                      }}
                    >
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        disabled={
                          typeof onOpenWriterStudio !==
                            "function"
                        }
                        onClick={() =>
                          onOpenWriterStudio?.({
                            chapterId:
                              activeChapter.id,
                            sceneId,
                            previewSceneTitle:
                              isStructurePreview
                                ? sceneTitle(
                                    scene,
                                    sceneIndex + 1,
                                    isFR
                                  )
                                : "",
                            previewSceneText:
                              isStructurePreview
                                ? sceneText(scene)
                                : "",
                            previewChapterTitle:
                              isStructurePreview
                                ? clean(
                                    activeChapter.title
                                  )
                                : "",
                          })
                        }
                        title={
                          isStructurePreview
                            ? (
                                isFR
                                  ? "Ouvrir cette scène dans Writer Studio en mode aperçu. Rien ne sera sauvegardé."
                                  : "Open this Scene in Writer Studio preview mode. Nothing will be saved."
                              )
                            : undefined
                        }
                      >
                        {isFR
                          ? "Écrire la scène"
                          : "Write Scene"}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          sceneIndex === 0
                        }
                        onClick={() =>
                          moveSceneWithin(
                            activeChapter.id,
                            sceneId,
                            "up"
                          )
                        }
                      >
                        {isFR
                          ? "Plus tôt"
                          : "Move Earlier"}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          sceneIndex ===
                          activeChapter
                            .sceneIds.length -
                            1
                        }
                        onClick={() =>
                          moveSceneWithin(
                            activeChapter.id,
                            sceneId,
                            "down"
                          )
                        }
                      >
                        {isFR
                          ? "Plus tard"
                          : "Move Later"}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          setMoveScenePositionId(
                            moving
                              ? ""
                              : sceneId
                          )
                        }
                      >
                        {isFR
                          ? "Déplacer à la position"
                          : "Move to Position"}
                      </button>

                      <label
                        style={{
                          display:
                            "inline-flex",
                          alignItems:
                            "center",
                          gap: 6,
                        }}
                      >
                        <span
                          className="ns-card-meta"
                          style={{
                            fontWeight: 800,
                          }}
                        >
                          {isFR
                            ? "Vers"
                            : "Move to"}
                        </span>

                        <select
                          className="ns-input"
                          value={
                            activeChapter.id
                          }
                          onChange={(event) =>
                            moveScene(
                              sceneId,
                              event.target
                                .value
                            )
                          }
                          style={{
                            width: "auto",
                            minWidth: 170,
                          }}
                        >
                          {chapters.map(
                            (chapter) => (
                              <option
                                key={
                                  chapter.id
                                }
                                value={
                                  chapter.id
                                }
                              >
                                {clean(
                                  chapter.title
                                )}
                              </option>
                            )
                          )}
                          <option value="unassigned">
                            {isFR
                              ? "Scènes non affectées"
                              : "Unassigned Scenes"}
                          </option>
                        </select>
                      </label>

                      {isStructurePreview ? (
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() =>
                            duplicateScene(
                              activeChapter.id,
                              sceneId
                            )
                          }
                        >
                          {isFR ? "Dupliquer" : "Duplicate"}
                        </button>
                      ) : (
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() =>
                            openSceneForEditing(sceneId)
                          }
                        >
                          {isFR ? "Modifier" : "Edit"}
                        </button>
                      )}

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          removeSceneFromChapter(
                            activeChapter.id,
                            sceneId
                          )
                        }
                      >
                        {isFR
                          ? "Retirer"
                          : "Remove"}
                      </button>
                    </div>
                  </div>

                  {moving ? (
                    <div
                      style={{
                        marginTop: 12,
                        display: "flex",
                        alignItems:
                          "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Nouvelle position"
                          : "New position"}
                      </strong>

                      <select
                        className="ns-input"
                        defaultValue={
                          String(
                            sceneIndex + 1
                          )
                        }
                        onChange={(event) =>
                          moveSceneToPosition(
                            activeChapter.id,
                            sceneId,
                            event.target.value
                          )
                        }
                        style={{
                          width: "auto",
                          minWidth: 120,
                        }}
                      >
                        {activeChapter
                          .sceneIds.map(
                            (_, index) => (
                              <option
                                key={
                                  index + 1
                                }
                                value={
                                  index + 1
                                }
                              >
                                {index + 1}
                              </option>
                            )
                          )}
                      </select>
                    </div>
                  ) : null}
                </article>
              );
            }
          )}

          {activeChapter.sceneIds.length ===
          0 ? (
            <section
              className="ns-card"
              style={{
                padding: 20,
                borderRadius: 18,
              }}
            >
              <strong>
                {isFR
                  ? "Aucune scène dans ce chapitre."
                  : "No Scenes in this Chapter yet."}
              </strong>
              <p
                className="ns-card-meta"
                style={{ marginBottom: 0 }}
              >
                {isFR
                  ? "Ajoutez une scène ici ou déplacez une scène existante depuis un autre chapitre."
                  : "Add a Scene here or move an existing Scene from another Chapter."}
              </p>
            </section>
          ) : null}
        </div>
        )}

        {unassignedSceneIds.length > 0 ? (
          <section
            className="ns-card"
            data-novel-story-unassigned-scenes="true"
            style={{
              padding: 18,
              borderRadius: 18,
              border:
                "1px dashed rgba(100,116,139,0.42)",
            }}
          >
            <details>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 900,
                }}
              >
                {isFR
                  ? "Scènes non affectées"
                  : "Unassigned Scenes"}{" "}
                · {unassignedSceneIds.length}
              </summary>

              {renderUnassignedSceneRows()}
            </details>
          </section>
        ) : null}
      </div>
    );
  }

  function renderBookSetup() {
    const genre =
      clean(story?.forum || project?.forum) ||
      (isFR ? "Non renseigné" : "Not set");

    const synopsis =
      clean(story?.synopsis || project?.synopsis) ||
      clean(story?.description || project?.description);

    const visibilityLabel =
      bookVisibility === "private_draft"
        ? (isFR ? "Brouillon privé" : "Private Draft")
        : bookVisibility === "nextscenes_readers"
        ? (isFR ? "Lecteurs NextScenes" : "NextScenes Readers")
        : bookVisibility === "published"
        ? (isFR ? "Publié" : "Published")
        : (isFR ? "Aperçu public" : "Public Preview");

    return (
      <section
        className="ns-novel-story-book-setup"
        data-novel-story-book-setup="true"
      >
        <div className="ns-novel-story-book-setup-intro">
          <div>
            <div className="ns-novel-story-book-setup-kicker">
              {isFR ? "PRÉPARER LE LIVRE" : "BOOK SETUP"}
            </div>
            <h3>
              {isFR
                ? "L’identité et la forme du livre"
                : "The identity and physical shape of the book"}
            </h3>
            <p>
              {isFR
                ? "Les choix créatifs restent simples pendant l’écriture. La couverture, le format et les réglages de publication vivent ici, au niveau du livre."
                : "Keep writing simple. Cover, format and publication choices belong here at book level."}
            </p>
          </div>

          <div className="ns-novel-story-book-setup-actions">
            <span className="ns-novel-story-book-setup-state"
              data-novel-book-setup-progress-status-v1="true">
              {bookSetupProgress.setupStatus} {isFR ? "enregistrées" : "saved"}
            </span>

            {!setupEditing ? (
              <button
                className="ns-button ns-button-primary"
                type="button"
                data-novel-story-edit-book-setup="true"
                onClick={beginBookSetupEdit}
              >
                {isFR
                  ? "Modifier Book Setup"
                  : "Edit Book Setup"}
              </button>
            ) : null}
          </div>
        </div>

        {story?.bookCategory === "children_book" && !setupEditing ? (
          <section className="ns-card" data-native-childrens-audience-v1f1="true" style={{padding:18}}>
            <h4>{isFR ? "Public du livre jeunesse" : "Children’s Book Audience"}</h4>
            <p>{isFR ? "Âge" : "Age"}: {story?.childrensBookProfile?.ageRange || "—"} · {story?.childrensBookProfile?.readingArrangement || "—"} · {story?.childrensBookProfile?.readingLevel || "—"}</p>
          </section>
        ) : null}
        {setupMessage ? (
          <div
            className="ns-novel-story-book-setup-message"
            role="status"
          >
            {setupMessage}
          </div>
        ) : null}

        {setupEditing && setupDraft ? (
          <section
            className="ns-card ns-novel-story-book-setup-editor"
            data-novel-story-book-setup-editor="true"
          >
            <div className="ns-novel-story-book-setup-editor-head">
              <div>
                <div className="ns-novel-story-setup-card-kicker">
                  {isFR ? "MODIFIER BOOK SETUP" : "EDIT BOOK SETUP"}
                </div>
                <h4>
                  {isFR
                    ? "Identité, couverture et format"
                    : "Identity, cover and format"}
                </h4>
              </div>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={cancelBookSetupEdit}
                disabled={setupSaving}
              >
                {isFR ? "Annuler" : "Cancel"}
              </button>
            </div>

            <div className="ns-novel-story-book-setup-form-grid">
              {story?.bookCategory === "children_book" ? (
                <>
                  {[
                    ["ageRange", isFR ? "Tranche d’âge" : "Reader age range", 80],
                    ["readingArrangement", isFR ? "Type de lecture" : "Reading arrangement", 100],
                    ["readingLevel", isFR ? "Niveau de lecture" : "Reading level", 100],
                    ["educationalObjective", isFR ? "Objectif pédagogique" : "Educational objective", 350],
                    ["themes", isFR ? "Thèmes" : "Themes", 350],
                    ["culturalSetting", isFR ? "Cadre culturel" : "Cultural setting", 180],
                    ["schoolGrade", isFR ? "Niveau scolaire" : "School grade", 100],
                  ].map(([field,label,limit]) => (
                    <label key={field} data-native-childrens-book-setup-field={field}>
                      <span>{label}</span>
                      <input className="ns-input" maxLength={limit}
                        required={["ageRange","readingArrangement","readingLevel"].includes(field)}
                        value={setupDraft.childrensBookProfile?.[field] || ""}
                        onChange={e=>updateSetupDraft("childrensBookProfile",{
                          ...(setupDraft.childrensBookProfile || {}), [field]:e.target.value,
                        })} />
                    </label>
                  ))}
                </>
              ) : null}
              <label>
                <span>{isFR ? "Titre du livre" : "Book title"}</span>
                <input
                  className="ns-input"
                  value={setupDraft.title || ""}
                  onChange={(event) =>
                    updateSetupDraft(
                      "title",
                      event.target.value
                    )
                  }
                />
              </label>

              <label data-novel-story-book-setup-printed-author="true">
                <span>{isFR ? "Nom d’auteur imprimé" : "Printed author name"}</span>
                <input className="ns-input"
                  maxLength={160}
                  value={setupDraft.printedAuthorName || ""}
                  onChange={(event) => updateSetupDraft(
                    "printedAuthorName", event.target.value)}
                />
                <small className="ns-card-meta">
                  {isFR
                    ? "Utilisé automatiquement sur la page de sous-titre du PDF. Il ne remplace pas le texte déjà saisi."
                    : "Automatically added to the PDF Subtitle page. Does not replace any text already written there."}
                </small>
              </label>

              <label>
                <span>{isFR ? "Genre" : "Genre"}</span>
                <input
                  className="ns-input"
                  value={setupDraft.forum || ""}
                  onChange={(event) =>
                    updateSetupDraft(
                      "forum",
                      event.target.value
                    )
                  }
                />
              </label>

              <label>
                <span>{isFR ? "Type d’histoire" : "Story type"}</span>
                <select
                  className="ns-input"
                  value={setupDraft.storyType || "longform"}
                  onChange={(event) =>
                    updateSetupDraft(
                      "storyType",
                      event.target.value
                    )
                  }
                >
                  <option value="longform">
                    {isFR
                      ? "Roman / récit long"
                      : "Novel / Long-form Story"}
                  </option>
                  <option value="short">
                    {isFR ? "Nouvelle" : "Short Story"}
                  </option>
                </select>
              </label>

              <label>
                <span>{isFR ? "Langue" : "Language"}</span>
                <select
                  className="ns-input"
                  value={setupDraft.language || "en"}
                  onChange={(event) =>
                    updateSetupDraft(
                      "language",
                      event.target.value
                    )
                  }
                >
                  <option value="en">
                    {isFR ? "Anglais" : "English"}
                  </option>
                  <option value="fr">
                    {isFR ? "Français" : "French"}
                  </option>
                  <option value="bilingual">
                    {isFR ? "Bilingue" : "Bilingual"}
                  </option>
                </select>
              </label>

              <label className="ns-novel-story-setup-form-wide">
                <span>{isFR ? "Synopsis" : "Synopsis"}</span>
                <textarea
                  className="ns-input"
                  value={setupDraft.synopsis || ""}
                  onChange={(event) =>
                    updateSetupDraft(
                      "synopsis",
                      event.target.value
                    )
                  }
                  style={{
                    maxWidth: "none",
                    minHeight: 120,
                    resize: "vertical",
                  }}
                />
              </label>

              <label className="ns-novel-story-setup-form-wide">
                <span>{isFR ? "Accroche du livre" : "Story Teaser"}</span>
                <p className="ns-card-meta" style={{ margin: "4px 0 8px", fontWeight: 400 }}>
                  {isFR
                    ? "Une invitation courte à lire, distincte du synopsis. L’accroche enregistrée sera visible dans Explore."
                    : "A short invitation to read, separate from the Synopsis. Your saved teaser appears in Explore."}
                </p>
                <textarea
                  className="ns-input"
                  aria-label={isFR ? "Accroche du livre" : "Story Teaser"}
                  data-novel-story-book-setup-teaser="true"
                  maxLength={900}
                  rows={4}
                  value={setupDraft.storyTeaser || ""}
                  onChange={(event) => updateSetupDraft("storyTeaser", event.target.value)}
                  placeholder={isFR
                    ? "Éveillez la curiosité sans dévoiler les secrets de l’histoire…"
                    : "Spark curiosity without giving away the story's secrets…"}
                  style={{ maxWidth: "none", minHeight: 100, resize: "vertical" }}
                />
                <span className="ns-card-meta" aria-live="polite" style={{ fontWeight: 400 }}>
                  {clean(setupDraft.storyTeaser).split(/\s+/).filter(Boolean).length} / 90 {isFR ? "mots" : "words"} ·
                  {" "}{String(setupDraft.storyTeaser || "").length} / 900 {isFR ? "caractères" : "characters"}
                </span>
              </label>

              <div className="ns-novel-story-cover-upload-grid ns-novel-story-setup-form-wide">
                <label className="ns-novel-story-cover-upload-field">
                  <span>{isFR ? "Couverture avant" : "Front cover"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setSetupCoverFile(
                        event.target.files?.[0] || null
                      )
                    }
                  />

                  {(setupCoverPreviewUrl ||
                  clean(setupDraft.coverImageUrl)) ? (
                    <div className="ns-novel-story-cover-upload-preview">
                      <img
                        src={
                          setupCoverPreviewUrl ||
                          clean(setupDraft.coverImageUrl)
                        }
                        alt={
                          isFR
                            ? "Aperçu de la couverture avant"
                            : "Front cover preview"
                        }
                      />
                      <span>
                        {setupCoverFile
                          ? (
                              isFR
                                ? "Sélectionnée · sera téléversée à l’enregistrement"
                                : "Selected · uploads when Book Setup is saved"
                            )
                          : (
                              isFR
                                ? "Couverture avant actuelle"
                                : "Current front cover"
                            )}
                      </span>
                    </div>
                  ) : (
                    <div className="ns-novel-story-cover-upload-preview" data-novel-working-upload-fallback="front" style={{gridTemplateColumns: "minmax(120px, 150px) minmax(0, 1fr)"}}>
                      <NovelWorkingCoverPairV1 side="front" title={setupDraft.title} authorName={setupDraft.printedAuthorName || bookAuthorName} isFR={isFR} />
                      <span>{isFR ? "Modèle provisoire · non enregistré" : "Working design · not saved"}</span>
                    </div>
                  )}

                  <small>
                    {setupCoverFile
                      ? setupCoverFile.name
                      : (
                          isFR
                            ? "Choisissez une image pour remplacer la couverture avant."
                            : "Choose an image to replace the front cover."
                        )}
                  </small>
                </label>

                <label className="ns-novel-story-cover-upload-field">
                  <span>{isFR ? "Quatrième de couverture" : "Back cover"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setSetupBackCoverFile(
                        event.target.files?.[0] || null
                      )
                    }
                  />

                  {(setupBackCoverPreviewUrl ||
                  clean(setupDraft.backCoverImageUrl)) ? (
                    <div className="ns-novel-story-cover-upload-preview">
                      <img
                        src={
                          setupBackCoverPreviewUrl ||
                          clean(setupDraft.backCoverImageUrl)
                        }
                        alt={
                          isFR
                            ? "Aperçu de la quatrième de couverture"
                            : "Back cover preview"
                        }
                      />
                      <span>
                        {setupBackCoverFile
                          ? (
                              isFR
                                ? "Sélectionnée · sera téléversée à l’enregistrement"
                                : "Selected · uploads when Book Setup is saved"
                            )
                          : (
                              isFR
                                ? "Quatrième de couverture actuelle"
                                : "Current back cover"
                            )}
                      </span>
                    </div>
                  ) : (
                    <div className="ns-novel-story-cover-upload-preview" data-novel-working-upload-fallback="back" style={{gridTemplateColumns: "minmax(120px, 150px) minmax(0, 1fr)"}}>
                      <NovelWorkingCoverPairV1 side="back" teaser={setupDraft.storyTeaser || setupDraft.synopsis} isFR={isFR} />
                      <span>{isFR ? "Modèle provisoire · non enregistré" : "Working design · not saved"}</span>
                    </div>
                  )}

                  <small>
                    {setupBackCoverFile
                      ? setupBackCoverFile.name
                      : (
                          isFR
                            ? "Choisissez une image pour remplacer la quatrième de couverture."
                            : "Choose an image to replace the back cover."
                        )}
                  </small>
                </label>
              </div>

              <label>
                <span>{isFR ? "Format de coupe" : "Trim size"}</span>
                <select
                  className="ns-input"
                  value={setupDraft.bookFormat?.trimSize || ""}
                  onChange={(event) =>
                    updateSetupFormat(
                      "trimSize",
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    {isFR ? "Choisir" : "Choose"}
                  </option>
                  <option value="5.5_x_8.5_in">5.5 × 8.5 inches</option>
                  <option value="6_x_9_in">6 × 9 inches</option>
                  <option value="8.25_x_8.25_in">8.25 × 8.25 inches</option>
                  <option value="8.5_x_8.5_in">8.5 × 8.5 inches</option>
                  <option value="8_x_10_in">8 × 10 inches</option>
                  <option value="8.5_x_11_in">8.5 × 11 inches</option>
                  <option value="a5">A5</option>
                  <option value="a4">A4</option>
                  <option value="custom_dimensions">
                    {isFR
                      ? "Dimensions personnalisées"
                      : "Custom dimensions"}
                  </option>
                  <option value="not_decided">
                    {isFR
                      ? "Pas encore décidé"
                      : "Not yet decided"}
                  </option>
                </select>
              </label>

              <label>
                <span>{isFR ? "Orientation" : "Orientation"}</span>
                <select
                  className="ns-input"
                  value={setupDraft.bookFormat?.orientation || ""}
                  onChange={(event) =>
                    updateSetupFormat(
                      "orientation",
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    {isFR ? "Choisir" : "Choose"}
                  </option>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">
                    {isFR ? "Paysage" : "Landscape"}
                  </option>
                  <option value="square">
                    {isFR ? "Carré" : "Square"}
                  </option>
                </select>
              </label>

              {setupDraft.bookFormat?.trimSize ===
              "custom_dimensions" ? (
                <label>
                  <span>
                    {isFR
                      ? "Dimensions personnalisées"
                      : "Custom dimensions"}
                  </span>
                  <input
                    className="ns-input"
                    value={
                      setupDraft.bookFormat
                        ?.customTrimSize || ""
                    }
                    placeholder="8.5 × 8.5 in"
                    onChange={(event) =>
                      updateSetupFormat(
                        "customTrimSize",
                        event.target.value
                      )
                    }
                  />
                </label>
              ) : null}

              <label>
                <span>{isFR ? "Fond perdu" : "Bleed"}</span>
                <select
                  className="ns-input"
                  value={setupDraft.bookFormat?.bleed || ""}
                  onChange={(event) =>
                    updateSetupFormat(
                      "bleed",
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    {isFR ? "Choisir" : "Choose"}
                  </option>
                  <option value="yes">
                    {isFR ? "Oui" : "Yes"}
                  </option>
                  <option value="no">
                    {isFR ? "Non" : "No"}
                  </option>
                </select>
              </label>

              <label>
                <span>
                  {isFR
                    ? "Format de publication"
                    : "Publication format"}
                </span>
                <select
                  className="ns-input"
                  value={
                    setupDraft.bookFormat
                      ?.publicationFormat || ""
                  }
                  onChange={(event) =>
                    updateSetupFormat(
                      "publicationFormat",
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    {isFR ? "Choisir" : "Choose"}
                  </option>
                  <option value="print">
                    {isFR ? "Imprimé" : "Print"}
                  </option>
                  <option value="digital">
                    {isFR ? "Numérique" : "Digital"}
                  </option>
                  <option value="print_and_digital">
                    {isFR
                      ? "Imprimé et numérique"
                      : "Print and digital"}
                  </option>
                </select>
              </label>
              {["print", "print_and_digital"].includes(setupDraft.bookFormat?.publicationFormat) ? (
                <>
                  <label>
                    <span>{isFR ? "Reliure" : "Binding"}</span>
                    <select className="ns-input"
                      data-novel-print-setting="binding"
                      value={setupDraft.bookFormat?.binding || ""}
                      onChange={(event) => updateSetupFormat("binding", event.target.value)}>
                      <option value="">{isFR ? "Choisir" : "Choose"}</option>
                      <option value="paperback">{isFR ? "Broché" : "Paperback"}</option>
                    </select>
                  </label>
                  <label>
                    <span>{isFR ? "Type de papier" : "Paper Type"}</span>
                    <select className="ns-input"
                      data-novel-print-setting="paperType"
                      value={setupDraft.bookFormat?.paperType || ""}
                      onChange={(event) => updateSetupFormat("paperType", event.target.value)}>
                      <option value="">{isFR ? "Choisir" : "Choose"}</option>
                      <option value="white">{isFR ? "Blanc" : "White"}</option>
                      <option value="cream">{isFR ? "Crème" : "Cream"}</option>
                    </select>
                  </label>
                  <label>
                    <span>{isFR ? "Couleur intérieure" : "Interior Colour"}</span>
                    <select className="ns-input"
                      data-novel-print-setting="interiorColor"
                      value={setupDraft.bookFormat?.interiorColor || ""}
                      onChange={(event) => updateSetupFormat("interiorColor", event.target.value)}>
                      <option value="">{isFR ? "Choisir" : "Choose"}</option>
                      <option value="black_and_white">{isFR ? "Noir et blanc" : "Black & White"}</option>
                      <option value="standard_color">{isFR ? "Couleur standard" : "Standard Colour"}</option>
                      <option value="premium_color">{isFR ? "Couleur premium" : "Premium Colour"}</option>
                    </select>
                  </label>
                  <p className="ns-card-meta" style={{ gridColumn: "1 / -1", margin: 0 }}>
                    {isFR
                      ? "Pour les éditions imprimées. La couleur standard ou premium exige du papier blanc. Les réglages restent modifiables avant la fabrication."
                      : "For printed editions. Standard or Premium Colour requires White paper. These settings do not enable print production."}
                  </p>
                </>
              ) : null}
            </div>

            <div className="ns-novel-story-book-setup-editor-actions">
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={() => saveBookSetup()}
                disabled={setupSaving}
              >
                {setupSaving
                  ? (
                      isFR
                        ? "Enregistrement…"
                        : "Saving…"
                    )
                  : (
                      isFR
                        ? "Enregistrer Book Setup"
                        : "Save Book Setup"
                    )}
              </button>

              <span className="ns-card-meta">
                {isFR
                  ? "Le mode d’écriture et la gouvernance restent gérés par les contrôles de collaboration existants."
                  : "Writing mode and governance remain managed by the existing collaboration controls."}
              </span>
            </div>
          </section>
        ) : null}

        <div className="ns-novel-story-book-setup-grid">
          <article className="ns-card ns-novel-story-setup-cover-card">
            <div className="ns-novel-story-setup-card-kicker">
              {isFR ? "COUVERTURE" : "BOOK COVER"}
            </div>

            <div className="ns-novel-story-cover-pair">
              <div className="ns-novel-story-cover-side">
                <strong>
                  {isFR ? "Avant" : "Front"}
                </strong>
                <div className="ns-novel-story-setup-cover-frame">
                  {bookCoverUrl ? (
                    <img
                      src={bookCoverUrl}
                      alt={
                        isFR
                          ? "Couverture avant actuelle"
                          : "Current front cover"
                      }
                    />
                  ) : story?.bookCategory === "children_book" ? (
                    <div data-native-childrens-cover-pending-v1f2c="front">
                      <NovelWorkingCoverPairV1
                        side="front" bookCategory="children_book"
                        title={storyTitle(story, project, isFR)}
                        authorName={bookAuthorName} isFR={isFR}
                      />
                      <p className="ns-card-meta" style={{textAlign:"center",margin:"8px 0 0"}}>
                        {isFR ? "Aperçu · couverture avant non ajoutée" : "Working preview · Front cover not uploaded"}
                      </p>
                    </div>
                  ) : (
                    <div data-novel-working-book-setup-cover="front">
                      <NovelWorkingCoverPairV1 side="front" title={storyTitle(story, project, isFR)} authorName={bookAuthorName} isFR={isFR} />
                    </div>
                  )}
                </div>
              </div>

              <div className="ns-novel-story-cover-side">
                <strong>
                  {isFR ? "Arrière" : "Back"}
                </strong>
                <div className="ns-novel-story-setup-cover-frame">
                  {bookBackCoverUrl ? (
                    <img
                      src={bookBackCoverUrl}
                      alt={
                        isFR
                          ? "Quatrième de couverture actuelle"
                          : "Current back cover"
                      }
                    />
                  ) : story?.bookCategory === "children_book" ? (
                    <div data-native-childrens-cover-pending-v1f2c="back">
                      <NovelWorkingCoverPairV1
                        side="back" bookCategory="children_book"
                        teaser={story?.storyTeaser || story?.synopsis} isFR={isFR}
                      />
                      <p className="ns-card-meta" style={{textAlign:"center",margin:"8px 0 0"}}>
                        {isFR ? "Aperçu · quatrième de couverture non ajoutée" : "Working preview · Back cover not uploaded"}
                      </p>
                    </div>
                  ) : (
                    <div data-novel-working-book-setup-cover="back">
                      <NovelWorkingCoverPairV1 side="back" teaser={story?.storyTeaser || story?.synopsis} isFR={isFR} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <p className="ns-card-meta">
              {isFR
                ? "Les couvertures avant et arrière se téléversent séparément dans Book Setup. L’aperçu de mise en page ne crée pas de couverture complète avec dos."
                : "Front and back covers upload separately in Book Setup. Book Layout Preview does not assemble a full cover with a spine."}
            </p>
            {bookSetupProgress.coverCount < 2 ? (
              <p className="ns-card-meta" data-novel-working-cover-upload-clarity-v1="true">
                {isFR
                  ? "Les illustrations sans liens de couvertures enregistrés sont des aperçus de travail, pas des couvertures enregistrées."
                  : "Cover illustrations shown without saved cover URLs are working previews, not saved covers."}
              </p>
            ) : null}
          </article>

          <article className="ns-card ns-novel-story-setup-card">
            <div className="ns-novel-story-setup-card-kicker">
              {isFR ? "IDENTITÉ DU LIVRE" : "BOOK IDENTITY"}
            </div>
            <h4>
              {storyTitle(story, project, isFR)}
            </h4>

            <dl className="ns-novel-story-setup-facts">
              <div>
                <dt>{isFR ? "Auteur" : "Author"}</dt>
                <dd>{bookAuthorName}</dd>
              </div>
              <div>
                <dt>{isFR ? "Type" : "Story type"}</dt>
                <dd>{bookStoryType}</dd>
              </div>
              <div>
                <dt>{isFR ? "Genre" : "Genre"}</dt>
                <dd>{genre}</dd>
              </div>
              <div>
                <dt>{isFR ? "Langue" : "Language"}</dt>
                <dd>{bookLanguage}</dd>
              </div>
            </dl>

            {synopsis ? (
              <p className="ns-card-meta ns-novel-story-setup-synopsis">
                {synopsis}
              </p>
            ) : null}

            {clean(story?.storyTeaser) ? (
              <div className="ns-novel-story-setup-teaser-preview" data-novel-story-book-setup-teaser-preview="true">
                <strong>{isFR ? "Accroche du livre" : "Story Teaser"}</strong>
                <p className="ns-card-meta" style={{ whiteSpace: "pre-wrap" }}>
                  {clean(story.storyTeaser)}
                </p>
              </div>
            ) : null}
          </article>

          <article className="ns-card ns-novel-story-setup-card">
            <div className="ns-novel-story-setup-card-kicker">
              {isFR ? "FORMAT & IMPRESSION" : "FORMAT & TRIM"}
            </div>
            <h4>
              {isFR
                ? "La forme physique du livre"
                : "The physical book format"}
            </h4>
            <p className="ns-card-meta">
              {isFR
                ? "Le format de coupe, l’orientation, le fond perdu et le type de publication seront définis ici en réutilisant les choix déjà éprouvés dans Children’s Book."
                : "Trim size, orientation, bleed and publication format will be set here, reusing the proven Children’s Book format pattern."}
            </p>

            <div className="ns-novel-story-setup-planned-row">
              <span>{isFR ? "Format de coupe" : "Trim size"}</span>
              <strong>
                {bookFormatLabel(
                  "trimSize",
                  savedBookFormat.trimSize
                )}
              </strong>
            </div>
            <div className="ns-novel-story-setup-planned-row">
              <span>{isFR ? "Orientation" : "Orientation"}</span>
              <strong>
                {bookFormatLabel(
                  "orientation",
                  savedBookFormat.orientation
                )}
              </strong>
            </div>
            <div className="ns-novel-story-setup-planned-row">
              <span>{isFR ? "Fond perdu" : "Bleed"}</span>
              <strong>
                {bookFormatLabel(
                  "bleed",
                  savedBookFormat.bleed
                )}
              </strong>
            </div>
            <div className="ns-novel-story-setup-planned-row">
              <span>
                {isFR
                  ? "Format de publication"
                  : "Publication format"}
              </span>
              <strong>
                {bookFormatLabel(
                  "publicationFormat",
                  savedBookFormat.publicationFormat
                )}
              </strong>
            </div>
            {["print", "print_and_digital"].includes(clean(savedBookFormat.publicationFormat)) ? (
              <>
                {[
                  ["binding", isFR ? "Reliure" : "Binding"],
                  ["paperType", isFR ? "Type de papier" : "Paper Type"],
                  ["interiorColor", isFR ? "Couleur intérieure" : "Interior Colour"],
                ].map(([field, caption]) => (
                  <div key={field} className="ns-novel-story-setup-planned-row">
                    <span>{caption}</span>
                    <strong>{bookFormatLabel(field, savedBookFormat[field])}</strong>
                  </div>
                ))}
              </>
            ) : null}
          </article>

          <article className="ns-card ns-novel-story-setup-card">
            <div className="ns-novel-story-setup-card-kicker">
              {isFR ? "ÉCRITURE & ACCÈS" : "WRITING & ACCESS"}
            </div>
            <h4>
              {isFR
                ? "Les règles du projet"
                : "Project rules"}
            </h4>

            <dl className="ns-novel-story-setup-facts">
              <div>
                <dt>{isFR ? "Mode d’écriture" : "Writing mode"}</dt>
                <dd>{bookWritingMode}</dd>
              </div>
              <div>
                <dt>{isFR ? "Visibilité" : "Visibility"}</dt>
                <dd>{visibilityLabel}</dd>
              </div>
              <div>
                <dt>{isFR ? "Chapitres" : "Chapters"}</dt>
                <dd>{chapters.length}</dd>
              </div>
              <div>
                <dt>{isFR ? "Scènes Canon" : "Canon scenes"}</dt>
                <dd>{sceneRecords.length}</dd>
              </div>
            </dl>

            <p className="ns-card-meta">
              {isFR
                ? "Les règles de collaboration et de gouvernance restent intactes. Book Setup les résume sans les remplacer."
                : "Collaboration and governance rules remain intact. Book Setup summarizes them without replacing them."}
            </p>
          </article>
        </div>

        <div className="ns-novel-story-book-setup-note">
          <strong>
            {isFR ? "Parcours de publication" : "Publication path"}
          </strong>
          <span>
            {isFR
              ? "Book Setup enregistre le format. Publication Preparation vérifie la préparation ; Book Layout Preview montre une mise en page provisoire. Seule une approbation finale distincte peut verrouiller un PDF intérieur."
              : "Book Setup saves the format. Publication Preparation checks readiness; Book Layout Preview shows a provisional layout. Only separate final approval can lock an interior PDF."}
          </span>
        </div>

        <div className="ns-novel-story-book-setup-next"
          data-novel-book-setup-optional-journey-v1="true">
          <div>
            <strong>
              {chapters.length === 0
                ? (isFR ? "Organiser les chapitres" : "Organize your chapters")
                : sceneRecords.length > 0
                ? (isFR ? "Poursuivre le manuscrit" : "Continue your manuscript")
                : (isFR ? "Prêt à commencer à écrire ?" : "Ready to start writing?")}
            </strong>
            <span>
              {isFR
                ? "Les couvertures ou le format peuvent être terminés plus tard. La structure du livre reste accessible."
                : "Cover uploads and format choices can be finished later. Book Structure remains available."}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="ns-button ns-button-primary" type="button"
              data-novel-story-book-setup-next="true"
              onClick={() => navigateBookWorkspace(chapters.length > 0 ? "write" : "book-structure")}
            >
              {chapters.length === 0
                ? (isFR ? "Organiser les chapitres →" : "Organize Chapters →")
                : sceneRecords.length > 0
                ? (isFR ? "Continuer à écrire →" : "Continue Writing →")
                : (isFR ? "Écrire la première scène →" : "Write First Scene →")}
            </button>
            {chapters.length > 0 ? (
              <button className="ns-button ns-button-outline" type="button"
                data-novel-book-setup-review-structure-v1="true"
                onClick={() => navigateBookWorkspace("book-structure")}
              >
                {isFR ? "Revoir la structure →" : "Review Book Structure →"}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  // Backend owner authorization remains authoritative. This extra UI gate
  // keeps contributor and sample-project workspaces free of owner actions.
  const previewActorId = clean(previewActor?.id || previewActor?._id);
  const savedOwnerId = clean(story?.authorId);
  const savedOwnerName = clean(previewActor?.username).toLowerCase();
  const ownsNovelForPreview = Boolean(
    !isStructurePreview && storyId && previewActorId && (
      savedOwnerId
        ? previewActorId === savedOwnerId
        : savedOwnerName && [story?.authorUsername, story?.createdBy, story?.author]
            .some((name) => clean(name).toLowerCase() === savedOwnerName)
    )
  );
  // Presentation safeguard only. The server independently checks the saved
  // Story _id, its owner, longform type, archive and explicit export flag.
  // Both the pilot _id and the opt-in frontend flag must be present.
  const finalPrintPilotEnabled = finalPrintPilotAvailableForBook({
    enabled: process.env.REACT_APP_NOVEL_FINAL_PRINT_APPROVAL_V1_ENABLED === "true",
    pilotStoryId: process.env.REACT_APP_NOVEL_FINAL_PRINT_APPROVAL_V1_PILOT_STORY_ID,
    canonicalStoryId: story?._id,
    storyType: story?.storyType,
    isOwner: ownsNovelForPreview,
  });

  // Advisory navigation reuses the same saved-source and format checks that
  // Publication Preparation uses. Nothing here marks review, PDF, or approval done.
  const journeyChecks = novelPublicationPreparationChecks({
    bookFormat: savedBookFormat, frontCoverUrl: bookCoverUrl,
    backCoverUrl: bookBackCoverUrl, chapters, sceneById, unassignedSceneIds,
  });
  const journeyManifest = buildNovelProductionSourceManifest({
    title: storyTitle(story, project, isFR), frontMatter: frontMatterItems,
    chapters, backMatter: backMatterItems, sceneById, unassignedSceneIds,
  });
  const journeyReadiness = novelWriterReadinessV3C3({
    readiness: journeyChecks, sourceManifest: journeyManifest,
    printPlanning: novelPrintSpecificationsPlanning(savedBookFormat),
    storyId, previewOnly: isStructurePreview,
  });

  async function openForewordPlacementDiagnostic() {
    if (forewordDiagnosticBusy || !ownsNovelForPreview || !interiorPreviewUrl) return;
    setForewordDiagnosticBusy(true);
    setForewordDiagnosticError("");
    setForewordDiagnosticReport(null);
    try {
      const found = await api.getNovelForewordPlacementDiagnostic(storyId);
      if (!found?.ok || found?.report?.schemaVersion !==
          "novel-foreword-placement-diagnostic-v1") {
        throw new Error(isFR
          ? "Diagnostic de l'avant-propos indisponible."
          : "Foreword diagnostic unavailable.");
      }
      setForewordDiagnosticReport(found.report);
    } catch (error) {
      setForewordDiagnosticError(
        clean(error?.message) ||
        (isFR ? "Diagnostic indisponible." : "Diagnostic unavailable.")
      );
    } finally {
      setForewordDiagnosticBusy(false);
    }
  }

  function openGatedFinalPrintApproval() {
    // A preview is not approval. The existing owner/pilot gate is required
    // before showing the already-protected exact-PDF review and approval UI.
    if (!ownsNovelForPreview || !finalPrintPilotEnabled) return;
    interiorPreviewRequestEpoch.current += 1;
    setInteriorPreviewBusy(false);
    setInteriorPreviewUrl("");
    setInteriorPreviewError("");
    setForewordDiagnosticReport(null);
    setForewordDiagnosticError("");
    setFinalPrintApprovalOpen(true);
  }

  async function openSavedNovelInteriorPreview() {
    if (interiorPreviewBusy || !ownsNovelForPreview) return;
    setFinalPrintApprovalOpen(false);
    setInteriorPreviewError("");
    setInteriorPreviewUrl("");
    setForewordDiagnosticReport(null);
    setForewordDiagnosticError("");
    const requestEpoch = ++interiorPreviewRequestEpoch.current;
    setInteriorPreviewBusy(true);
    try {
      // Fresh owner-only source check: never use unsaved editor state or a
      // fingerprint cached from a previous book revision.
      const source = await api.getNovelSavedInteriorStatus(storyId);
      if (requestEpoch !== interiorPreviewRequestEpoch.current) return;
      if (!source?.ok || !source?.sourceReady || !source?.fingerprint) {
        const first = Array.isArray(source?.issues) ? source.issues[0] : null;
        throw new Error(
          clean(first?.message || first?.error || first?.code || first) ||
          (isFR
            ? "Enregistrez et vérifiez le manuscrit avant son aperçu PDF."
            : "Save and verify the manuscript before opening the PDF preview.")
        );
      }
      const pdf = await api.getNovelPrivateInteriorPreview(storyId, source.fingerprint);
      if (requestEpoch !== interiorPreviewRequestEpoch.current) return;
      setInteriorPreviewUrl(URL.createObjectURL(pdf));
    } catch (failure) {
      if (requestEpoch !== interiorPreviewRequestEpoch.current) return;
      const disabled = failure?.status === 404 &&
        /not enabled|not available|désactiv/i.test(String(failure?.message || ""));
      setInteriorPreviewError(disabled
        ? (isFR
          ? "L’aperçu PDF privé n’est pas encore activé dans cet environnement."
          : "Private PDF preview is not enabled in this environment yet.")
        : clean(failure?.message) ||
          (isFR ? "Impossible d’ouvrir cet aperçu privé." : "Could not open the private preview."));
    } finally {
      if (requestEpoch === interiorPreviewRequestEpoch.current) {
        setInteriorPreviewBusy(false);
      }
    }
  }

  function navigateBookWorkspace(stage, options = {}) {
    if (isChildrensBridge && stage === "setup") {
      onChildrensBookBookSetup?.();
      return;
    }
    setFinalPrintApprovalOpen(false);
    // A new workspace selection replaces the private preview (including a pending request).
    interiorPreviewRequestEpoch.current += 1;
    setInteriorPreviewBusy(false);
    setInteriorPreviewUrl("");
    setInteriorPreviewError("");
    setForewordDiagnosticReport(null);
    setForewordDiagnosticError("");
    // Switching workspaces closes a previous whole-book reading overlay.
    setWholeBookReviewOpen(false);
    // New workspace navigation clears the context unless this destination was
    // explicitly opened from the preparation checklist.
    setPublicationPreparationReturnActive(options.fromPublicationPreparation === true);

    if (stage === "review") {
      setActiveChapterId("");
      setActiveMatter("manuscript");
      setManuscriptWorkspace("review");
      return;
    }

    if (stage === "publication-preparation") {
      setActiveChapterId("");
      setActiveMatter("manuscript");
      setManuscriptWorkspace("publication-preparation");
      return;
    }

    if (stage === "publication-proof") {
      setActiveChapterId("");
      setActiveMatter("manuscript");
      setManuscriptWorkspace("publication-proof");
      return;
    }

    if (stage === "setup") {
      setActiveChapterId("");
      setActiveMatter("setup");
      return;
    }

    if (stage === "book-structure") {
      setActiveChapterId("");
      setActiveMatter("manuscript");
      setManuscriptWorkspace("book-structure");
      return;
    }

    if (stage === "write") {
      setActiveChapterId("");
      setActiveMatter("manuscript");
      setManuscriptWorkspace("write");
    }
  }

  if (loading) {
    return (
      <section
        className="ns-card"
        data-novel-story-book-view-draft="true"
        style={{
          padding: 24,
          borderRadius: 20,
        }}
      >
        {isFR
          ? "Chargement de la structure du livre…"
          : "Loading the Book View draft…"}
      </section>
    );
  }

  return (
    <section
      className="ns-novel-story-book-view"
      data-novel-story-book-view-draft="true"
      style={{
        display: "grid",
        gap: 18,
      }}
    >
      {isChildrensBridge ? (
        <NovelStoryWorkspaceNavigator
          childrensBookUseNovelStagesV1E4D={true}
          bookKind="childrens-book"
          childrensBookAudienceReady={childrensBookAudienceReady}
          onChildrensBookBookSetup={onChildrensBookBookSetup}
          projectTitle={storyTitle(story,project,isFR)}
          wordCount={totalWords}
          activeMatter={activeMatter}
          manuscriptWorkspace={manuscriptWorkspace}
          frontMatterCount={childrensBookMatterCounts ? childrensBookMatterCounts.front : frontMatterItems.length}
          chapterCount={chapters.length}
          sceneCount={sceneRecords.length}
          backMatterCount={childrensBookMatterCounts ? childrensBookMatterCounts.back : backMatterItems.length}
          reviewEnabled={false} proofEnabled={false} preparationEnabled={false}
          onNavigate={navigateBookWorkspace}
          onOpenWriting={chapters.length === 1 && typeof onOpenWriterStudio === "function"
            ? () => continueChapterWriting(chapters[0]) : undefined}
          onBack={onBack} isFR={isFR}
        />
      ) : isStructurePreview ? (
        <div
          className="ns-card"
          style={{
            padding: 22,
            borderRadius: 20,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              minWidth: 0,
              flex: "1 1 440px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#2563eb",
              }}
            >
              {isFR
                ? "APERÇU DE STRUCTURE · AUCUNE SAUVEGARDE"
                : "STRUCTURE PREVIEW · NOTHING IS SAVED"}
            </div>

            <h2
              className="ns-section-title"
              style={{
                margin: "6px 0 7px",
              }}
            >
              {storyTitle(
                story,
                project,
                isFR
              )}
            </h2>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Cet aperçu réutilise les modèles Structure du livre et Écrire déjà éprouvés dans Textbook. Rien n’est enregistré."
                : "This preview reuses the proven Textbook Book Structure and Write patterns. Nothing is saved."}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onBack}
          >
            ← {isFR
              ? "Retour aux projets"
              : "Back to Projects"}
          </button>
        </div>
      ) : (
        <NovelJourneyPilotWorkspaceV2C2
          story={story}
          storyId={storyId}
          isStructurePreview={isStructurePreview}
          isOwner={ownsNovelForPreview}
          journeyReadiness={journeyReadiness}
          unassignedCanonCount={unassignedSceneIds.length}
          chapterCount={chapters.length}
          sceneCount={sceneRecords.length}
          publicationStatus={story?.publicationStatus || project?.lifecycle?.publicationStatus || "inDevelopment"}
          previewEnabled={ownsNovelForPreview}
          previewBusy={interiorPreviewBusy}
          finalApprovalEnabled={finalPrintPilotEnabled}
          isFR={isFR}
        >
          {(pilotJourney) => (
        <NovelStoryWorkspaceNavigator
          pilotJourney={pilotJourney}
          nativeChildrensBookV1F2={story?.bookCategory === "children_book"}
          projectTitle={storyTitle(
            story,
            project,
            isFR
          )}
          coverImageUrl={bookCoverUrl}
          backCoverImageUrl={bookBackCoverUrl}
          bookFormat={savedBookFormat}
          wordCount={totalWords}
          activeMatter={activeMatter}
          manuscriptWorkspace={manuscriptWorkspace}
          frontMatterCount={frontMatterItems.length}
          chapterCount={chapters.length}
          sceneCount={sceneRecords.length}
          backMatterCount={backMatterItems.length}
          publicationStatus={story?.publicationStatus || project?.lifecycle?.publicationStatus || "inDevelopment"}
          journeyReadiness={journeyReadiness}
          reviewEnabled={!isStructurePreview}
          proofEnabled={!isStructurePreview}
          preparationEnabled={!isStructurePreview}
          finalApprovalEnabled={finalPrintPilotEnabled}
          finalApprovalActive={finalPrintApprovalOpen}
          onFinalApproval={openGatedFinalPrintApproval}
          previewEnabled={ownsNovelForPreview}
          previewBusy={interiorPreviewBusy}
          previewActive={Boolean(interiorPreviewBusy || interiorPreviewUrl || interiorPreviewError)}
          onPreview={openSavedNovelInteriorPreview}
          onNavigate={navigateBookWorkspace}
          onOpenWriting={
            !isStructurePreview &&
            chapters.length === 1 &&
            typeof onOpenWriterStudio === "function"
              ? () => continueChapterWriting(chapters[0])
              : undefined
          }
          onBack={onBack}
          isFR={isFR}
        />
          )}
        </NovelJourneyPilotWorkspaceV2C2>
      )}

      {finalPrintPilotEnabled && finalPrintApprovalOpen ? (
          <NovelFinalPrintApprovalV1 storyId={storyId} isFR={isFR}
            onClose={() => setFinalPrintApprovalOpen(false)} />
        ) : null}

      {ownsNovelForPreview && (interiorPreviewError || interiorPreviewUrl) ? (
        <section className="ns-card" data-novel-private-interior-preview="owner-only"
          style={{ padding: "16px 18px", borderRadius: 16, display: "grid", gap: 10,
            border: "1px solid #cbd5e1" }}>
          {interiorPreviewError ? (
            <p role="alert" style={{ margin: 0, color: "#9a3412" }}>{interiorPreviewError}</p>
          ) : null}
          {interiorPreviewUrl ? (
            <div style={{ display: "grid", gap: 9 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, flexWrap: "wrap" }}>
                <strong>{isFR ? "Aperçu privé · non définitif" : "Private preview · not final"}</strong>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className="ns-button ns-button-outline"
                    data-novel-foreword-readonly-diagnostic="true"
                    disabled={forewordDiagnosticBusy}
                    onClick={openForewordPlacementDiagnostic}>
                    {forewordDiagnosticBusy
                      ? (isFR ? "Analyse…" : "Inspecting…")
                      : (isFR ? "Diagnostic de l’avant-propos" : "Diagnose Foreword")}
                  </button>
                  <button type="button" className="ns-button ns-button-outline"
                    onClick={() => {
                      setInteriorPreviewUrl("");
                      setForewordDiagnosticReport(null);
                      setForewordDiagnosticError("");
                    }}>
                    {isFR ? "Fermer l’aperçu" : "Close Preview"}
                  </button>
                </div>
              </div>
              <NovelPrivatePreviewFinalHandoffV2C20
                storyId={storyId}
                isOwner={ownsNovelForPreview}
                privatePreviewPresent={Boolean(interiorPreviewUrl)}
                finalPrintPilotEnabled={finalPrintPilotEnabled}
                previewBusy={interiorPreviewBusy}
                isFR={isFR}
                onOpenFinalApproval={openGatedFinalPrintApproval}
              />
              {forewordDiagnosticError ? (
                <p role="alert" style={{ margin: 0, color: "#9a3412" }}>
                  {forewordDiagnosticError}
                </p>
              ) : null}
              {forewordDiagnosticReport ? (
                <details open data-novel-foreword-structure-report="owner-only">
                  <summary style={{ fontWeight: 750, cursor: "pointer" }}>
                    {isFR ? "Rapport privé des sauts de page" : "Private Foreword Page-Break Report"}
                  </summary>
                  <p style={{ margin: "8px 0", fontSize: 13 }}>
                    {isFR
                      ? "Structure et mesures seulement — aucun texte du manuscrit ni identifiant."
                      : "Structure and measured page fragments only — no manuscript text or identifiers."}
                  </p>
                  <pre style={{ overflowX: "auto", whiteSpace: "pre-wrap",
                    wordBreak: "break-word", maxHeight: 360, padding: 12,
                    border: "1px solid #cbd5e1", borderRadius: 8,
                    fontSize: 12, background: "#f8fafc" }}>
                    {JSON.stringify(forewordDiagnosticReport, null, 2)}
                  </pre>
                </details>
              ) : null}
              <iframe title={isFR ? "Aperçu intérieur du roman" : "Novel interior PDF preview"}
                src={interiorPreviewUrl}
                style={{ width: "100%", height: "min(78vh, 900px)", minHeight: 390,
                  border: "1px solid #cbd5e1", borderRadius: 10 }}/>
            </div>
          ) : null}
        </section>
      ) : null}

      {error ? (
        <div
          className="ns-card"
          role="alert"
          style={{
            padding: 16,
            borderRadius: 16,
            color: "#b91c1c",
            fontWeight: 750,
          }}
        >
          {error}
        </div>
      ) : null}

      <nav
        className="ns-novel-story-matter-tabs"
        data-active-matter={activeMatter}
        aria-label={
          isFR
            ? "Structure du livre"
            : "Book structure"
        }
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          style={buttonStyle(
            activeMatter === "front"
          )}
          onClick={() =>
            setActiveMatter("front")
          }
        >
          {isFR
            ? "Préliminaires"
            : "Front Matter"}
        </button>

        <button
          type="button"
          style={buttonStyle(
            activeMatter ===
              "manuscript"
          )}
          onClick={() =>
            setActiveMatter("manuscript")
          }
        >
          {isFR
            ? "Manuscrit"
            : "Manuscript"}
        </button>

        <button
          type="button"
          style={buttonStyle(
            activeMatter === "back"
          )}
          onClick={() =>
            setActiveMatter("back")
          }
        >
          {isFR
            ? "Postliminaires"
            : "Back Matter"}
        </button>
      </nav>

      {publicationPreparationReturnActive && !isStructurePreview &&
        (activeMatter === "setup" ||
          (activeMatter === "manuscript" &&
            (manuscriptWorkspace === "review" || manuscriptWorkspace === "publication-proof" ||
              manuscriptWorkspace === "book-structure"))) ? (
        <aside role="note" data-novel-publication-return-v3c3b="true"
          style={{ padding: "14px 18px", borderRadius: 14,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, background: "#f0fdf4", border: "1px solid #a7d7bd",
            borderLeft: "5px solid #047857" }}>
          <div>
            <strong style={{ color: "#065f46" }}>
              {isFR ? "Votre parcours de publication" : "Continue preparing your book"}
            </strong>
            <p className="ns-card-meta" style={{ margin: "4px 0 0" }}>
              {isFR
                ? "Après cette étape, revenez directement à Préparation de la publication."
                : "When you finish here, return directly to Publication Preparation to continue."}
            </p>
          </div>
          <button type="button" className="ns-button ns-button-primary"
            data-novel-publication-return-action-v3c3b="true"
            onClick={() => navigateBookWorkspace("publication-preparation")}>
            {isFR ? "← Retour à la préparation" : "← Back to Publication Preparation"}
          </button>
        </aside>
      ) : null}

      {activeMatter === "setup" && !isChildrensBridge
        ? renderBookSetup()
        : null}

      {activeMatter === "front"
        ? renderMatterSection("front")
        : null}

      {activeMatter === "back"
        ? renderMatterSection("back")
        : null}

      {activeMatter === "manuscript" ? (
        <div
          data-novel-story-manuscript="true"
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          {manuscriptWorkspace !==
          "chapter" ? (
            <div
              data-novel-story-manuscript-summary="true"
              className="ns-card-meta"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 6,
                flexWrap: "wrap",
                fontWeight: 750,
              }}
            >
              {chapters.length}{" "}
              {isFR
                ? (chapters.length === 1 ? "chapitre" : "chapitres")
                : (chapters.length === 1 ? "chapter" : "chapters")}{" "}
              · {sceneRecords.length}{" "}
              {isFR
                ? (sceneRecords.length === 1 ? "scène" : "scènes")
                : (sceneRecords.length === 1 ? "scene" : "scenes")}{" "}
              · {totalWords.toLocaleString()}{" "}
              {isFR
                ? "mots"
                : "words"}
            </div>
          ) : null}

          {wholeBookReviewOpen
            ? renderWholeBookReview()
            : manuscriptWorkspace === "publication-proof"
            ? (
              <NovelStoryPublicationProof
                isChildrensBook={story?.bookCategory === "children_book"}
                title={storyTitle(story, project, isFR)}
                authorName={bookAuthorName}
                bookFormat={savedBookFormat}
                frontCoverUrl={bookCoverUrl}
                backCoverUrl={bookBackCoverUrl}
                frontMatter={frontMatterItems}
                chapters={chapters}
                backMatter={backMatterItems}
                sceneById={sceneById}
                unassignedSceneIds={unassignedSceneIds}
                isFR={isFR}
                // Respect the existing Publication Preparation entry context.
                // Returning from a provisional layout preview never confirms
                // preparation, layout, final PDF or publication.
                returnToPreparation={publicationPreparationReturnActive}
                onReturn={() => navigateBookWorkspace(
                  publicationPreparationReturnActive ? "publication-preparation" : "review"
                )}
                onOpenBookSetup={() => navigateBookWorkspace("setup")}
                onOpenChapter={(chapterId) => openChapter(chapterId)}
                onOpenMatter={(kind, itemId) => {
                  setMatterEditorKind(kind);
                  setMatterEditorItemId(itemId);
                }}
              />
            )
            : manuscriptWorkspace === "publication-preparation"
            ? (
              <NovelStoryPublicationPreparation
                title={storyTitle(story, project, isFR)}
                authorName={bookAuthorName}
                storyId={storyId}
                previewOnly={isStructurePreview}
                bookFormat={savedBookFormat}
                frontCoverUrl={bookCoverUrl}
                backCoverUrl={bookBackCoverUrl}
                frontMatter={frontMatterItems}
                chapters={chapters}
                backMatter={backMatterItems}
                sceneById={sceneById}
                unassignedSceneIds={unassignedSceneIds}
                isFR={isFR}
                onOpenBookSetup={() => navigateBookWorkspace("setup", { fromPublicationPreparation: true })}
                onOpenBookStructure={() => navigateBookWorkspace("book-structure", { fromPublicationPreparation: true })}
                onOpenReview={() => navigateBookWorkspace("review", { fromPublicationPreparation: true })}
                onOpenProof={() => navigateBookWorkspace("publication-proof", { fromPublicationPreparation: true })}
              />
            )
            : manuscriptWorkspace === "review"
            ? (
              <NovelStoryBookReview
                title={storyTitle(story, project, isFR)}
                chapters={chapters}
                frontMatter={frontMatterItems}
                backMatter={backMatterItems}
                unassignedSceneIds={unassignedSceneIds}
                sceneById={sceneById}
                isFR={isFR}
                onOpenSection={(sectionId) => {
                  setWholeBookReviewSection(sectionId);
                  setWholeBookReviewOpen(true);
                }}
                onOpenChapter={(chapterId) => openChapter(chapterId)}
                onBackToBookStructure={() => {
                  setManuscriptWorkspace("book-structure");
                }}
              />
            )
            : manuscriptWorkspace === "overview"
            ? null
            : manuscriptWorkspace ===
              "book-structure"
            ? renderBookStructure()
            : manuscriptWorkspace ===
              "write"
            ? renderWriteChapters()
            : renderSceneStructure()}
        </div>
      ) : null}

      {renderSceneEditor()}

      {renderMatterEditor()}

      <NextScenesConfirmDialog
        open={Boolean(pendingSetupCoverConfirmation)}
        title={pendingSetupCoverConfirmation?.replaceFront || pendingSetupCoverConfirmation?.replaceBack
          ? (isFR ? "Remplacer la couverture existante ?" : "Replace the current cover image?")
          : novelBookCoverConfirmationCopyV1(pendingSetupCoverConfirmation, isFR).title}
        message={novelBookCoverConfirmationCopyV1(pendingSetupCoverConfirmation, isFR).message}
        stayLabel={isFR ? "Annuler" : "Cancel"}
        leaveLabel={isFR ? "Enregistrer les couvertures choisies" : "Save Selected Cover"}
        onStay={() => setPendingSetupCoverConfirmation(null)}
        onLeave={() => {
          const pending = pendingSetupCoverConfirmation;
          setPendingSetupCoverConfirmation(null);
          if (!pending?.required || setupSaving || !setupEditing || !setupDraft) return;
          const current = novelBookCoverConfirmationPlanV1({
            frontFile: setupCoverFile, backFile: setupBackCoverFile,
            replaceFront: Boolean(setupCoverFile && clean(setupDraft.coverImageUrl)),
            replaceBack: Boolean(setupBackCoverFile && clean(setupDraft.backCoverImageUrl)),
          });
          // A stale dialog cannot authorize a different file or new saved image state.
          if (current.required && pending.frontFile === current.frontFile &&
              pending.backFile === current.backFile &&
              pending.replaceFront === current.replaceFront &&
              pending.replaceBack === current.replaceBack) {
            void saveBookSetup({ coverReplacementConfirmed: true });
          }
        }}
      />

      <NextScenesConfirmDialog
        open={Boolean(pendingMatterRemoval)}
        title={
          isFR
            ? "Supprimer cet élément du livre ?"
            : "Remove this book matter item?"
        }
        message={
          (() => {
            const pending = pendingMatterRemoval;
            const item = pending
              ? matterItemsFor(pending.kind).find(
                  (candidate) => candidate.id === pending.itemId
                )
              : null;
            const title = clean(item?.title) ||
              (isFR ? "cet élément" : "this item");
            return isFR
              ? `« ${title} » sera retiré du livre avec son texte et sa mise en forme. Cette action ne peut pas être annulée.`
              : `“${title}” and its saved text and formatting will be removed from the book. This cannot be undone.`;
          })()
        }
        stayLabel={isFR ? "Annuler" : "Cancel"}
        leaveLabel={isFR ? "Supprimer l’élément" : "Remove Item"}
        onStay={() => setPendingMatterRemoval(null)}
        onLeave={async () => {
          const pending = pendingMatterRemoval;
          setPendingMatterRemoval(null);
          if (!pending || savingMatter) return;
          await removeMatterItem(pending.kind, pending.itemId);
        }}
      />

      <NextScenesConfirmDialog
        open={Boolean(pendingChapterRemovalId)}
        title={
          isFR
            ? "Retirer ce chapitre ?"
            : "Remove this Chapter?"
        }
        message={
          (() => {
            const chapter =
              chapters.find(
                (item) =>
                  item.id ===
                  pendingChapterRemovalId
              );

            const title =
              clean(chapter?.title) ||
              (isFR
                ? "ce chapitre"
                : "this Chapter");

            const sceneCount =
              Array.isArray(
                chapter?.sceneIds
              )
                ? chapter.sceneIds.length
                : 0;

            if (isFR) {
              return sceneCount > 0
                ? `${title} sera retiré de la structure du livre. Ses ${sceneCount} scène${sceneCount === 1 ? "" : "s"} seront déplacées vers les scènes non affectées et ne seront pas supprimées.`
                : `${title} sera retiré de la structure du livre. Aucune scène ne sera supprimée.`;
            }

            return sceneCount > 0
              ? `${title} will be removed from Book Structure. Its ${sceneCount} Scene${sceneCount === 1 ? "" : "s"} will move to Unassigned Scenes and will not be deleted.`
              : `${title} will be removed from Book Structure. No Scenes will be deleted.`;
          })()
        }
        stayLabel={
          isFR ? "Annuler" : "Cancel"
        }
        leaveLabel={
          isFR
            ? "Retirer le chapitre"
            : "Remove Chapter"
        }
        onStay={cancelRemoveChapter}
        onLeave={confirmRemoveChapter}
      />
    </section>
  );
}
